import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { parseISO, startOfDay } from 'date-fns';
import { Task, TaskLocation, TaskLocationType, QuantifiedTask, TaskCosting, TaskStage, TaskType } from '../../../../types/Task';
import { taskService, TaskCreateInput } from '../../../../services/quantifiedTaskService';
import { ForemanArea } from '../../../../types/ForemanArea';
import { activityService, ActivityQueryParams } from '../../../../services/activityService';
import { Activity } from '../../../../types/Activity';
import { projectService } from '../../../../services/projectService';
import { Project } from '../../../../types/Project';
import { Asset, AssetType } from '../../../../types/Asset';
import { ASSET_TYPE_LABELS } from '../../../../constants/assetTypes';
import { assetService } from '../../../../services/assetService';
import { TASK_STATUSES } from '../statusBar';
import StatusBar from '../statusBar';
import { attachmentService } from '../../../../services/attachmentService';
import { TaskAttachment } from '../../../../types/Task';
import { useAuth } from '../../../../contexts/AuthContext';
import ResponsiveModal from '../ResponsiveModal';
import { ContractArea } from '../../../../types/ContractArea';
import TextArea from '../TextArea';
import { Button, ButtonGroup, Form, Alert, Card, InputGroup, Spinner, Table, Row, Col, ListGroup, Modal } from '@/components/ui';
import { areasService, type QuantifiedPlan } from '../../../../services/areasService';
import type { ContractArea as ServiceContractArea } from '../../../../types/ContractArea';  
import { SearchableDropdown } from '../../../../components/SearchableDropdown';
import MultiTierDropdown from '../../../../components/MultiTierDropdown';
import { usePermissions } from '../../../../hooks/usePermissions';
import GoogleGPSMap from '../../../../components/GoogleGPSMap';
import { effortService, CreateEffortInput } from '../../../../services/effortService';
import { calculateActualTotalValue, getEarliestEffortDate } from '../../utils';
import DistanceDisplay from '../../../../components/DistanceDisplay';
import TaskImageAttachments from './TaskImageAttachments';
import TaskEffortsTable from '../../../../components/TaskEffortsTable';
import RelatedWorkIdsTab, { hasLinkedWorkIds } from '../../../../components/RelatedWorkIdsTab';
import type { Effort } from '../../../../types/Effort';
import { getFieldLockingState, getLockedFieldStyle, getLockedButtonStyle, getLockedActiveButtonStyle, canEditInternalReview } from '../../../../utils/fieldLocking';
import {
  formatContractYearDateMismatchMessage,
  getContractYearDateMismatch,
} from '../../utils/contractYearDateValidation';
import type { QuantifiedInitialData } from '../../../work-id/utils/workIdToTaskInitialData';
import { employeeService } from '../../../../services/employeeService';
import { User } from '../../../../types/User';
import { MultiSelectDropdown } from '../../../../components/MultiSelectDropdown';
import { normalizeToUTCDateString, formatDateForDisplayUTC, getCurrentUTCDateString, yyyyMmDdToISONoonUTC } from '../../../../utils/dateUtils';
import { formatDollars } from '../../../../utils/formatDollars';
import { sortAssetsAlphabetically } from '../../../../utils/assetUtils';
import {
  getScheduleVehicles,
  getScheduleResources,
  type ScheduleVehicle,
  type ScheduleResource,
} from '../../../../services/scheduleService';
import {
  buildVehicleTypeOptions,
  buildUnitToTypeMap,
  countVehicleTypeDemandByDay,
  filterVehiclesForPlanScope,
  inferUniqueTypesFromUnits,
  isVehicleTypeSelectionOverCapacity,
  pickRepresentativeUnitsForTypes,
} from '../../utils/equipmentAvailability';
import { shouldForceActualTotalValueToZero, allowsActualTotalValueOverride } from '../../../../utils/taskCostingActualTotalValueLifecycle';
import { confirmAction, LoadErrorState } from '@/components/ui';
import { describeActionError } from '../../../../utils/loadError';

// Add interface for Other Cost entries
interface OtherCost {
  id?: number | string;
  date: string;
  description: string;
  cost: number | string;
  markupPercent: number | string;
  markedUpCost: number;
  comments?: string;
}

// Helper function to get current date in local timezone (YYYY-MM-DD format)
const getCurrentLocalDateString = (): string => {
  const today = new Date();
  const localDate = new Date(today.getTime() - (today.getTimezoneOffset() * 60000));
  return localDate.toISOString().split('T')[0];
};

// Helper function to parse date string as local date (avoiding timezone issues)
const parseLocalDate = (dateString: string | null | undefined): Date => {
  if (!dateString || typeof dateString !== 'string') {
    return new Date(); // Return current date if invalid
  }
  
  // Handle ISO date strings (e.g., "2025-08-01T19:00:00.000Z")
  if (dateString.includes('T') || dateString.includes('Z')) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }
    console.warn('Invalid ISO date format:', dateString);
    return new Date(); // Return current date if invalid
  }
  
  // Check if it's a valid YYYY-MM-DD format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) {
    console.warn('Invalid date format:', dateString);
    return new Date(); // Return current date if invalid format
  }
  
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    console.warn('Invalid date:', dateString);
    return new Date(); // Return current date if invalid
  }
  
  return date;
};




const SCHEDULE_DEPENDENCIES_MARKER = '<!--SCHEDULE_DEPS:';
const DEPENDENCY_TRIGGER_OPTIONS = ['Weather', 'Permit', 'Environmental Restriction', 'Other'] as const;

type DependencyTrigger = (typeof DEPENDENCY_TRIGGER_OPTIONS)[number];
type ScheduleDependenciesPayload = {
  triggers: string[];
  otherComment?: string | null;
  resourceDependentType?: string | null;
  completionTaskId?: number | null;
};

function extractScheduleDependenciesFromComments(raw: string | null | undefined): {
  cleanComments: string;
  dependencies: ScheduleDependenciesPayload | null;
} {
  const value = String(raw || '');
  const markerStart = value.indexOf(SCHEDULE_DEPENDENCIES_MARKER);
  if (markerStart < 0) return { cleanComments: value, dependencies: null };
  const jsonStart = markerStart + SCHEDULE_DEPENDENCIES_MARKER.length;
  const markerEnd = value.indexOf('-->', jsonStart);
  if (markerEnd < 0) return { cleanComments: value, dependencies: null };
  const jsonText = value.slice(jsonStart, markerEnd).trim();
  const cleanComments = `${value.slice(0, markerStart)}${value.slice(markerEnd + 3)}`.trim();
  try {
    const parsed = JSON.parse(jsonText) as ScheduleDependenciesPayload;
    const triggers = Array.isArray(parsed?.triggers)
      ? parsed.triggers.map((t) => String(t || '').trim()).filter(Boolean)
      : [];
    if (triggers.length === 0) return { cleanComments, dependencies: null };
    return {
      cleanComments,
      dependencies: {
        triggers,
        otherComment: parsed.otherComment ? String(parsed.otherComment) : null,
        resourceDependentType: parsed.resourceDependentType ? String(parsed.resourceDependentType) : null,
        completionTaskId:
          parsed.completionTaskId != null && Number.isFinite(Number(parsed.completionTaskId))
            ? Number(parsed.completionTaskId)
            : null,
      },
    };
  } catch {
    return { cleanComments, dependencies: null };
  }
}

function mergeScheduleDependenciesIntoComments(
  comments: string | null | undefined,
  payload: ScheduleDependenciesPayload | null
): string | null {
  const clean = extractScheduleDependenciesFromComments(comments).cleanComments.trim();
  if (!payload || !Array.isArray(payload.triggers) || payload.triggers.length === 0) {
    return clean || null;
  }
  const normalized: ScheduleDependenciesPayload = {
    triggers: payload.triggers,
    otherComment: payload.otherComment ? payload.otherComment.trim() : null,
    resourceDependentType: payload.resourceDependentType ? payload.resourceDependentType.trim() : null,
    completionTaskId: payload.completionTaskId ?? null,
  };
  const encoded = `${SCHEDULE_DEPENDENCIES_MARKER}${JSON.stringify(normalized)}-->`;
  return clean ? `${clean}\n${encoded}` : encoded;
}

export type TaskSavedOptions = { isNew?: boolean; keepOpen?: boolean };

interface TaskModalProps {
  visible: boolean;
  onClose: () => void;
  task?: Task;
  onTaskSaved: (updatedTask: Task, options?: TaskSavedOptions) => void;
  foremanAreas: ForemanArea[];
  project?: Project;
  taskType: TaskType;
  initialContractAreas: ContractArea[];
  defaultStatus?: string;
  initialDataFromWorkId?: QuantifiedInitialData;
  /** Dialog title for a new task; matches the button that opened it (V2-036). */
  addTitle?: string;
}

type TabType =
  | 'basic'
  | 'location'
  | 'planned_accomplishment'
  | 'actual_accomplishment'
  | 'attachments'
  | 'other_costs'
  | 'related_work_ids'
  | 'efforts';

/**
 * Fields validated on save (FORM-01): each error shows directly under its field, every tab holding
 * errors shows a count, and save moves to the first one. Order = the order the fields appear.
 */
type TaskFieldKey =
  | 'name'
  | 'contractYear'
  | 'foremanArea'
  | 'project'
  | 'identifiedDate'
  | 'plannedCompletionDate'
  | 'assignedVehicleTypes'
  | 'dependencyOtherComment'
  | 'activityId'
  | 'identifiedQuantity'
  | 'unitOfMeasure'
  | 'total'
  | 'baseRate'
  | 'startLocation';

type TaskFieldErrors = Partial<Record<TaskFieldKey, string>>;

const TASK_FIELD_ORDER: TaskFieldKey[] = [
  'name',
  'contractYear',
  'foremanArea',
  'project',
  'identifiedDate',
  'plannedCompletionDate',
  'assignedVehicleTypes',
  'dependencyOtherComment',
  'activityId',
  'identifiedQuantity',
  'unitOfMeasure',
  'total',
  'baseRate',
  'startLocation',
];

const TASK_FIELD_TAB: Record<TaskFieldKey, TabType> = {
  name: 'basic',
  contractYear: 'basic',
  foremanArea: 'basic',
  project: 'basic',
  identifiedDate: 'basic',
  plannedCompletionDate: 'basic',
  assignedVehicleTypes: 'basic',
  dependencyOtherComment: 'basic',
  activityId: 'planned_accomplishment',
  identifiedQuantity: 'planned_accomplishment',
  unitOfMeasure: 'planned_accomplishment',
  total: 'planned_accomplishment',
  baseRate: 'planned_accomplishment',
  startLocation: 'location',
};

const taskFieldId = (key: TaskFieldKey) => `task-modal-field-${key}`;

/** Moves focus to a field (or the first focusable control inside its wrapper). */
const focusTaskField = (key: TaskFieldKey) => {
  const el = document.getElementById(taskFieldId(key));
  if (!el) return;
  const target = el.matches('input, select, textarea, button')
    ? el
    : el.querySelector<HTMLElement>('input:not([type="hidden"]), select, textarea, button');
  const focusEl = target || el;
  focusEl.focus();
  focusEl.scrollIntoView?.({ block: 'center' });
};

const COST_TYPES = {
  FIXED_COST: 'Fixed Cost',
  COST_PLUS: 'Cost Plus'
} as const;

const AVAILABLE_STATUSES = ['PROPOSED', 'PLANNED', 'ACTIVE'] as const;



type AssetTypeEnum = AssetType;

interface FormData {
  id?: number;
  activityId?: number;
  description?: string;
  instructions?: string;
  name?: string;
  rateMultiplier: number | string;
  priority?: string;
  status: TaskStage;
  taskType: 'Unit Rate' | 'Lump Sum';
  costType: 'Fixed Cost' | 'Cost Plus';
  rateAdjustmentComments: string;
  labourMarkup: number;
  materialMarkup: number;
  subcontractorMarkup: number;
  vehicleMarkup: number;
  hiredEquipmentMarkup: number;
  project?: string;
  foremanArea?: string;
  contractYear?: string;
  identifiedDate?: string;
  completionDate?: string;
  completionStartDate?: string;
  identifiedQuantity?: number | string;
  unitOfMeasure?: string;
  baseRate?: number | string;
  rateAdjustment?: number | string;
  calculatedRate?: number | string;
  total?: number | string;
  identifiedAccomplishment?: number | string;
  completionAccomplishment?: number | string;
  startLatitude?: number | string;
  startLongitude?: number | string;
  endLatitude?: number | string;
  endLongitude?: number | string;
  assetId?: number | null;
  assetType?: AssetTypeEnum;
  comments?: string | null;
  contractArea?: string;
  activityNumber?: string;
  taskNumber?: string;
  estimatedEffortHours?: number | string;
  plannedCompletionDate?: string;
  assignedEmployeeIds?: number[];
  /** Vehicle/equipment types (not unit numbers) for Work Planning; mapped to units on save. */
  assignedVehicleTypes?: string[];
  dependencyTriggers?: string[];
  dependencyOtherComment?: string;
  dependencyResourceType?: string;
  dependencyCompletionTaskId?: string;
}

const RequiredLabel: React.FC<{ text: string }> = ({ text }) => (
  <Form.Label>
    {text} <span className="tw-text-danger">*</span>
  </Form.Label>
);

const shouldShowRateComments = (formData: FormData, selectedActivity?: Activity | null) => {
  if (formData.taskType === 'Lump Sum') {
    return false;
  }

  const { scheduled2Rate } = extractActivityCostSnapshot(selectedActivity);
  const baseRateChanged =
    Number(formData.baseRate) !== (scheduled2Rate ?? 0);
  const multiplierChanged = Number(formData.rateMultiplier) !== 1;
  const hasAdjustment = Number(formData.rateAdjustment) > 0;

  return baseRateChanged || multiplierChanged || hasAdjustment;
};

// Add this near the top of the file, after the imports
const readOnlyStyle = {
  backgroundColor: '#e9ecef',
  cursor: 'not-allowed'
};

interface AttachmentNoteModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (note: string) => void;
  fileName: string;
}

const AttachmentNoteModal: React.FC<AttachmentNoteModalProps> = ({
  visible,
  onClose,
  onSave,
  fileName
}) => {
  const [note, setNote] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(note);
    setNote('');
  };

  // Nested inside the main TaskModal: Headless UI keeps a dialog stack, so this portal mounts
  // after its parent and paints above it — no manual z-index bump needed.
  return (
    <Modal show={visible} onHide={onClose} mobileVariant="center">
      <Modal.Header closeButton>
        <Modal.Title>Add Note to Attachment</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit} className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <Modal.Body>
          <p className="tw-mb-3">
            Add a note for: <strong>{fileName}</strong>
          </p>
          <Form.Group className="tw-mb-3">
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Enter a note for this attachment..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Save Note
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

// Add this near the top with other constants
const SERVICE_AREA_MARKUPS = {
  '22': {
    labourMarkup: 60,
    materialMarkup: 10,
    vehicleMarkup: 15,
    subcontractorMarkup: 10,
    hiredEquipmentMarkup: 15
  },
  '16': {
    labourMarkup: 60,
    materialMarkup: 10,
    vehicleMarkup: 15,
    subcontractorMarkup: 10,
    hiredEquipmentMarkup: 15
  },
  '25': {
    labourMarkup: 60,
    materialMarkup: 10,
    vehicleMarkup: 15,
    subcontractorMarkup: 10,
    hiredEquipmentMarkup: 15
  },
  '17': {
    labourMarkup: 60,
    materialMarkup: 10,
    vehicleMarkup: 15,
    subcontractorMarkup: 10,
    hiredEquipmentMarkup: 15
  }
};

const formatCurrency = (value: string | number) => {
  if (!value && value !== 0) return '';
  const num = Number(value);
  return isNaN(num) ? value : `$${num.toFixed(2)}`;
};

const stripCurrency = (value: string) => {
  // Only remove currency symbols and spaces, keep all digits, decimal points, and minus signs
  return value.replace(/[^0-9.-]/g, '');
};

/** True when an API save error is about planned dates / contract-year date range. */
const isPlannedDateRelatedError = (message: string): boolean => {
  const lower = message.toLowerCase();
  return (
    lower.includes('planned start') ||
    lower.includes('planned completion') ||
    lower.includes('planned date') ||
    lower.includes('fall outside') ||
    (lower.includes('contract year') &&
      (lower.includes('date') || lower.includes('range') || lower.includes('plan')))
  );
};

const initialFormState: FormData = {
  description: '',
  priority: 'LOW',
  status: TaskStage.PROPOSED,
  taskType: 'Unit Rate',
  costType: 'Fixed Cost',
  rateAdjustmentComments: '',
  labourMarkup: 0,
  materialMarkup: 0,
  subcontractorMarkup: 0,
  vehicleMarkup: 0,
  hiredEquipmentMarkup: 0,
  project: '',
  rateMultiplier: 1,
  contractArea: '',
  contractYear: '',
  foremanArea: '',
  activityId: undefined,
  identifiedDate: '',
  completionDate: '',
  completionStartDate: '',
  identifiedQuantity: '',
  unitOfMeasure: '',
  baseRate: '',
  rateAdjustment: '',
  startLatitude: '',
  startLongitude: '',
  endLatitude: '',
  endLongitude: '',
  assetId: null,
  assetType: undefined,
  calculatedRate: '',
  total: '',
  identifiedAccomplishment: '0',
  completionAccomplishment: '0',
  taskNumber: '',
  estimatedEffortHours: '',
  plannedCompletionDate: '',
  assignedVehicleTypes: [],
  dependencyTriggers: [],
  dependencyOtherComment: '',
  dependencyResourceType: '',
  dependencyCompletionTaskId: '',
};

// Add type conversion helper
const convertContractArea = (area: ServiceContractArea): ContractArea => ({
  id: area.id,
  name: area.name,
  code: area.code || '',  // Provide empty string as default for code
  foremanAreas: [] // Provide an empty array that matches the ForemanArea type
});

// Add this function after the other utility functions
const generateTaskName = (activity: Activity | undefined, asset: Asset | undefined): string => {
  if (!activity) return '';
  
  let name = `${activity.activityName} (#${activity.activityNumber})`;
  
  if (asset) {
    const assetName = asset.description?.trim() || asset.name?.trim() || 'Asset';
    name += ` on ${assetName} (#${asset.assetNumber})`;
  }
  
  return name;
};

const extractActivityCostSnapshot = (activity?: Activity | null) => {
  const costing = activity?.activityCostings?.[0];
  return {
    serviceAreaId: activity?.serviceAreaId ?? costing?.serviceAreaId ?? null,
    contractYear: activity?.contractYear ?? costing?.contractYear ?? null,
    scheduled2Rate: activity?.scheduled2Rate ?? costing?.scheduled2Rate ?? null,
  };
};

/** Merge Work ID prepopulation into existing form without resetting unrelated fields. */
const mergeQuantifiedWorkIdIntoFormData = (
  prev: FormData,
  workIdData: QuantifiedInitialData,
  preserveName: boolean
): FormData => {
  const startLoc = workIdData.taskLocations?.[0];
  return {
    ...prev,
    contractYear: workIdData.contractYear ?? prev.contractYear,
    ...(workIdData.activityId != null ? { activityId: workIdData.activityId } : {}),
    unitOfMeasure: workIdData.unitOfMeasure ?? prev.unitOfMeasure,
    identifiedQuantity: workIdData.identifiedQuantity?.toString() ?? prev.identifiedQuantity,
    foremanArea: workIdData.foremanAreaId?.toString() ?? prev.foremanArea,
    contractArea: workIdData.contractAreaId?.toString() ?? prev.contractArea,
    ...(!preserveName && workIdData.name ? { name: workIdData.name } : {}),
    ...(startLoc
      ? {
          startLatitude: startLoc.latitude?.toString() ?? prev.startLatitude,
          startLongitude: startLoc.longitude?.toString() ?? prev.startLongitude,
          assetId: startLoc.assetId ?? prev.assetId,
        }
      : {}),
  };
};

const TaskModal: React.FC<TaskModalProps> = ({
  visible,
  onClose,
  task,
  onTaskSaved,
  foremanAreas = [], // Add default empty array
  project: initialProject,
  taskType: initialTaskType,
  addTitle = 'Add Task',
  initialContractAreas,
  defaultStatus,
  initialDataFromWorkId
}): JSX.Element | null => {
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  /** Per-field validation errors (FORM-01). */
  const [fieldErrors, setFieldErrors] = useState<TaskFieldErrors>({});
  /** The value each errored field had when its error was raised; editing it clears the error. */
  const fieldErrorSnapshotRef = useRef<Partial<Record<TaskFieldKey, string>>>({});
  /** Upload / delete failures on the File Attachments tab. */
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  /** Project list failed to load (shown under the Project field, with Retry). */
  const [projectsLoadError, setProjectsLoadError] = useState(false);
  const [projectsReloadKey, setProjectsReloadKey] = useState(0);
  const showRelatedWorkIdsTab = !!task?.id && hasLinkedWorkIds(task);
  // The tab disappears once the last Work ID is unlinked; fall back to Basic Info.
  useEffect(() => {
    if (activeTab === 'related_work_ids' && !showRelatedWorkIdsTab) setActiveTab('basic');
  }, [activeTab, showRelatedWorkIdsTab]);
  /** Fresh efforts for the Efforts tab (refetch on open so OT breakdown matches DB after form approval). */
  const [effortsTabRows, setEffortsTabRows] = useState<Effort[]>([]);
  const [effortsTabLoading, setEffortsTabLoading] = useState(false);
  const lastProcessedTaskIdRef = useRef<number | undefined>(undefined);
  const isTaskNameManuallyEditedRef = useRef(false);
  /** Ensures we apply active-plan default once per foreman area for new tasks (see contract-year effect). */
  const contractYearDefaultAppliedRef = useRef(false);
  const newTaskInitializedRef = useRef(false);
  const workIdDataMergedRef = useRef(false);
  const activitiesFetchSeqRef = useRef(0);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [showMap, setShowMap] = useState(false);
  const [locationType, setLocationType] = useState<'start' | 'end'>('start');
  const [selectedRouteInfo, setSelectedRouteInfo] = useState<{ distance: number; duration: number } | null>(null);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assetLoading, setAssetLoading] = useState(false);
  
  // Add new state for road assets
  const [roadAssets, setRoadAssets] = useState<Asset[]>([]);
  const [roadAssetsLoading, setRoadAssetsLoading] = useState(false);
  
  // Add new state for grouped assets
  const [groupedAssets, setGroupedAssets] = useState<{ [key: string]: Asset[] }>({});

  // Add projectId to props
  const projectId = task?.projectId;
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Add new state for contract areas
  const [contractAreas, setContractAreas] = useState<ContractArea[]>([]);
  const [selectedContractArea, setSelectedContractArea] = useState<string>('');

  const [quantifiedPlans, setQuantifiedPlans] = useState<QuantifiedPlan[]>([]);
  const [loadingQuantifiedPlans, setLoadingQuantifiedPlans] = useState(false);

  // Add state for editable Actual Total Value
  const [actualTotalValueInput, setActualTotalValueInput] = useState<string | undefined>(undefined);
  const [isActualTotalValueManuallyEdited, setIsActualTotalValueManuallyEdited] = useState<boolean>(false);

  // Add state for Other Costs management
  const [otherCosts, setOtherCosts] = useState<OtherCost[]>([]);
  const [editingOtherCost, setEditingOtherCost] = useState<OtherCost | null>(null);
  const [showOtherCostModal, setShowOtherCostModal] = useState(false);
  const [otherCostsLoading, setOtherCostsLoading] = useState(false);
  const [otherCostsLoadError, setOtherCostsLoadError] = useState(false);
  const [otherCostsLoadedTaskId, setOtherCostsLoadedTaskId] = useState<number | null>(null);

  // Add state for employees
  const [employees, setEmployees] = useState<User[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Add state for planned vehicles (Assigned Equipments)
  const [availableVehicles, setAvailableVehicles] = useState<ScheduleVehicle[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [scheduleResourcesForAvailability, setScheduleResourcesForAvailability] = useState<
    ScheduleResource[] | null
  >(null);
  const [loadingScheduleAvailability, setLoadingScheduleAvailability] = useState(false);
  const [equipmentAvailabilityFetchFailed, setEquipmentAvailabilityFetchFailed] = useState(false);
  const [dependencyTaskOptions, setDependencyTaskOptions] = useState<Array<{ id: number; label: string }>>([]);
  /** Avoid overwriting user edits when inferring types from saved task units. */
  const vehicleTypesSyncedKeyRef = useRef<string>('');

  // Check if user is Finance Reporter (read-only access)
  const isFinanceReporter = currentUser?.role?.name === 'Finance Reporter';
  const hasEditPermissions = hasPermission('quantified:task_create') || hasPermission('quantified:task_edit');
  const isReadOnlyForFinanceReporter = isFinanceReporter && !hasEditPermissions;

  // Add new function to filter assets based on search query
  const filterAssetsBySearch = (assets: Asset[], query: string) => {
    if (!query) return assets;
    const lowerQuery = query.toLowerCase();

    return assets.filter(asset => {
      // Check asset's own properties
      const assetMatches = 
        (asset.description?.toLowerCase().includes(lowerQuery) ||
        asset.name?.toLowerCase().includes(lowerQuery) ||
        asset.assetNumber?.toLowerCase().includes(lowerQuery));

      if (assetMatches) return true;

      // Check parent asset information if available
      if (asset.parentAsset) {
        const parentAsset = roadAssets.find(ra => 
          ra.assetNumber?.trim() === asset.parentAsset?.trim()
        );
        
        if (parentAsset) {
          return (
            parentAsset.description?.toLowerCase().includes(lowerQuery) ||
            parentAsset.name?.toLowerCase().includes(lowerQuery) ||
            parentAsset.assetNumber?.toLowerCase().includes(lowerQuery)
          );
        }
      }

      return false;
    });
  };

  useEffect(() => {
    const loadSelectedProject = async () => {
      
      if (initialProject) {
        if (!task?.id && initialProject.status?.toLowerCase() === 'completed') {
          setSelectedProject(null);
          setFormData(prev => ({
            ...prev,
            project: '',
          }));
          return;
        }
        setSelectedProject(initialProject);
        setFormData(prev => {
          const newFormData = {
            ...prev,
            project: initialProject.id.toString()
          };
          return newFormData;
        });
      } else if (projectId) {
        try {
          const project = await projectService.getProjectById(projectId);
          setSelectedProject(project);
          setFormData(prev => {
            const newFormData = {
              ...prev,
              project: project.id.toString()
            };
            return newFormData;
          });
        } catch (err) {
          console.error('Error loading project:', err);
        }
      } else {
      }
    };
    
    // Only run this effect when the modal is visible to avoid race conditions
    if (visible) {
      loadSelectedProject();
    }
  }, [initialProject, projectId, visible, task?.id]);

  // Add this computed value for filtered activities
  const filteredActivities = useMemo(() => {
    // Ensure activities is an array
    if (!Array.isArray(activities)) {
      console.warn("Activities is not an array:", activities);
      return [];
    }
    
    // Find the selected foreman area
    if (!formData.contractYear || !formData.foremanArea) {
      return activities;
    }

    const selectedForemanArea = foremanAreas.find(
      area => area.id === Number(formData.foremanArea)
    );

    if (!selectedForemanArea) {
      return activities;
    }

    const expectedYear = Number(formData.contractYear);

    const filtered = activities.filter(activity => {
      const snapshot = extractActivityCostSnapshot(activity);
      const matchesYear =
        snapshot.contractYear === undefined ||
        snapshot.contractYear === null ||
        Number.isNaN(expectedYear)
          ? true
          : snapshot.contractYear === expectedYear;

      const matchesServiceArea =
        snapshot.serviceAreaId === undefined ||
        snapshot.serviceAreaId === null
          ? true
          : snapshot.serviceAreaId === selectedForemanArea.serviceAreaId;

      return matchesYear && matchesServiceArea;
    });

    return filtered.length > 0 ? filtered : activities;
  }, [activities, formData.contractYear, formData.foremanArea, foremanAreas]);


  const { openContractYears, legacyContractYear } = useMemo(() => {
    // All Open plan years for the service area (user can pick any; default is the active one).
    const openYears = Array.from(
      new Set(quantifiedPlans.filter((p) => p.status === 'Open').map((p) => p.year))
    ).sort((a, b) => b - a);
    const cy = formData.contractYear?.trim();
    const n = cy ? Number(cy) : NaN;
    // If the task's current year is not among open years, show it as a legacy option.
    const legacy =
      cy &&
      Number.isFinite(n) &&
      n > 0 &&
      !openYears.includes(n)
        ? {
            year: n,
            labelSuffix: quantifiedPlans.some((p) => p.year === n && p.status === 'Closed')
              ? '(Closed plan)'
              : '(current)',
          }
        : null;
    return { openContractYears: openYears, legacyContractYear: legacy };
  }, [quantifiedPlans, formData.contractYear]);

  const contractYearDateMismatch = useMemo(
    () =>
      getContractYearDateMismatch({
        isStanding: task?.mode?.toLowerCase() === 'standing',
        contractYear: formData.contractYear,
        identifiedDate: formData.identifiedDate,
        plannedCompletionDate: formData.plannedCompletionDate,
        quantifiedPlans,
      }),
    [
      task?.mode,
      formData.contractYear,
      formData.identifiedDate,
      formData.plannedCompletionDate,
      quantifiedPlans,
    ]
  );

  useEffect(() => {
    if (!visible || !formData.foremanArea) {
      setQuantifiedPlans([]);
      return;
    }
    const fa = foremanAreas.find((a) => a.id === Number(formData.foremanArea));
    const serviceAreaId = fa?.serviceAreaId;
    if (!serviceAreaId) {
      setQuantifiedPlans([]);
      return;
    }
    let cancelled = false;
    setLoadingQuantifiedPlans(true);
    areasService
      .getQuantifiedPlans(serviceAreaId)
      .then((plans) => {
        if (!cancelled) setQuantifiedPlans(plans);
      })
      .catch(() => {
        if (!cancelled) setQuantifiedPlans([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingQuantifiedPlans(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, formData.foremanArea, foremanAreas]);

  useEffect(() => {
    contractYearDefaultAppliedRef.current = false;
  }, [formData.foremanArea]);

  /** New tasks: default contract year to the active Open plan year (dropdown still lists all Open years). */
  useEffect(() => {
    if (!visible || task || !formData.foremanArea || loadingQuantifiedPlans) return;

    const workIdPinsContractYear =
      initialDataFromWorkId != null &&
      initialDataFromWorkId.contractYear != null &&
      String(initialDataFromWorkId.contractYear).trim() !== '';
    if (workIdPinsContractYear || contractYearDefaultAppliedRef.current) {
      return;
    }

    const openYears = quantifiedPlans.filter((p) => p.status === 'Open').map((p) => p.year);
    const activePlan =
      quantifiedPlans.find((p) => p.status === 'Open' && p.isActive) ??
      quantifiedPlans.find((p) => p.isActive);
    if (!activePlan || !openYears.includes(activePlan.year)) return;

    const preferredYear = activePlan.year;
    const y = Number(formData.contractYear);
    if (y === preferredYear) {
      contractYearDefaultAppliedRef.current = true;
      return;
    }

    contractYearDefaultAppliedRef.current = true;
    setFormData((prev) => ({ ...prev, contractYear: String(preferredYear) }));
  }, [
    visible,
    task,
    formData.foremanArea,
    loadingQuantifiedPlans,
    quantifiedPlans,
    formData.contractYear,
    initialDataFromWorkId,
  ]);


  // Update the activeProjects computed value
  const activeProjects = useMemo(() => {
    if (!Array.isArray(projects)) {
      console.warn("Projects is not an array:", projects);
      return [];
    }
    return projects.filter(project => 
      project.status === 'active' || 
      project.status === 'planned' ||
      (task?.projectId && project.id === task.projectId)
    );
  }, [projects, task?.projectId]);

  // Add this near the top of the component with other useMemo hooks
  const filteredProjects = useMemo(() => {
    const initialOkForNewTask =
      !initialProject ||
      Boolean(task?.id) ||
      initialProject.status?.toLowerCase() !== 'completed';

    // Always include the initialProject if it's provided (from ProjectDetailsPage)
    let projectsToShow = activeProjects;
    
    // If we have an initialProject, make sure it's included
    if (initialProject && initialOkForNewTask && !activeProjects.find(p => p.id === initialProject.id)) {
      projectsToShow = [...activeProjects, initialProject];
    }
    
    if (!formData.foremanArea) {
      // If we have an initialProject, prioritize it by putting it first
      if (initialProject) {
        const otherProjects = projectsToShow.filter(p => p.id !== initialProject.id);
        return [initialProject, ...otherProjects];
      }
      return projectsToShow;
    }

    if (!Array.isArray(foremanAreas)) {
      console.warn("ForemanAreas is not an array:", foremanAreas);
      return projectsToShow;
    }

    const selectedForemanArea = foremanAreas.find(
      area => area.id === Number(formData.foremanArea)
    );

    if (!selectedForemanArea) {
      return projectsToShow;
    }

    const serviceAreaId = selectedForemanArea.serviceAreaId;
    if (!serviceAreaId) {
      return projectsToShow;
    }


    const filtered = projectsToShow.filter(project => {
      const matches = project.serviceAreaId === serviceAreaId;
      
      // Always include the task's current project when editing
      const isCurrentTaskProject = task?.projectId && project.id === task.projectId;
      
      // Always include the initialProject when provided (from ProjectDetailsPage) and allowed
      const isInitialProject =
        Boolean(initialProject) &&
        initialOkForNewTask &&
        project.id === initialProject!.id;
      
      if (!matches && !isCurrentTaskProject && !isInitialProject) {
      }
      return matches || isCurrentTaskProject || isInitialProject;
    });

    return filtered;
  }, [activeProjects, formData.foremanArea, foremanAreas, task?.projectId, initialProject, task?.id]);

  const selectedPlanServiceAreaId = useMemo(() => {
    if (!formData.foremanArea) return null;
    const fa = foremanAreas.find((a) => a.id === Number(formData.foremanArea));
    return fa?.serviceAreaId ?? null;
  }, [formData.foremanArea, foremanAreas]);

  const vehicleCapacityContext = useMemo(() => {
    const scoped = filterVehiclesForPlanScope(availableVehicles, selectedPlanServiceAreaId);
    const byType = buildVehicleTypeOptions(scoped);
    const capacityByType = new Map<string, number>();
    byType.forEach((o) => capacityByType.set(o.type, o.capacity));

    if (
      !scheduleResourcesForAvailability ||
      !formData.foremanArea ||
      !formData.identifiedDate ||
      !formData.plannedCompletionDate
    ) {
      return { scoped, byType, capacityByType, demandByDay: null as ReturnType<typeof countVehicleTypeDemandByDay> | null, rangeStart: null as Date | null, rangeEnd: null as Date | null };
    }

    const start = parseISO(formData.identifiedDate);
    const end = parseISO(formData.plannedCompletionDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { scoped, byType, capacityByType, demandByDay: null as ReturnType<typeof countVehicleTypeDemandByDay> | null, rangeStart: null as Date | null, rangeEnd: null as Date | null };
    }
    const rangeStart = startOfDay(start);
    const rangeEnd = startOfDay(end);
    if (rangeEnd.getTime() < rangeStart.getTime()) {
      return { scoped, byType, capacityByType, demandByDay: null as ReturnType<typeof countVehicleTypeDemandByDay> | null, rangeStart: null as Date | null, rangeEnd: null as Date | null };
    }

    const unitToType = buildUnitToTypeMap(availableVehicles);
    const demandByDay = countVehicleTypeDemandByDay(
      scheduleResourcesForAvailability,
      unitToType,
      rangeStart,
      rangeEnd,
      Number(formData.foremanArea),
      task?.id
    );
    return { scoped, byType, capacityByType, demandByDay, rangeStart, rangeEnd };
  }, [
    availableVehicles,
    selectedPlanServiceAreaId,
    scheduleResourcesForAvailability,
    formData.foremanArea,
    formData.identifiedDate,
    formData.plannedCompletionDate,
    task?.id,
  ]);

  const vehicleEquipmentTypeOptions = useMemo(() => {
    if (
      !vehicleCapacityContext.demandByDay ||
      !vehicleCapacityContext.rangeStart ||
      !vehicleCapacityContext.rangeEnd
    ) {
      return vehicleCapacityContext.byType.map((o) => ({
        type: o.type,
        capacity: o.capacity,
        availableCapacity: o.capacity,
      }));
    }

    const out = vehicleCapacityContext.byType.map((o) => {
      let maxUsed = 0;
      vehicleCapacityContext.demandByDay!.forEach((typeMap) => {
        maxUsed = Math.max(maxUsed, typeMap.get(o.type) || 0);
      });
      const availableCapacity = Math.max(0, o.capacity - maxUsed);
      return { type: o.type, capacity: o.capacity, availableCapacity };
    });
    return out;
  }, [vehicleCapacityContext]);

  const equipmentAvailabilityMessage = useMemo(() => {
    const msg =
      'This equipment is not available in the selected service area for the chosen dates. Please adjust the dates or select a different equipment type.';
    if (!formData.assignedVehicleTypes?.length) return null;
    if (equipmentAvailabilityFetchFailed) return null;
    if (!scheduleResourcesForAvailability || loadingScheduleAvailability) return null;
    if (!formData.foremanArea || !formData.identifiedDate || !formData.plannedCompletionDate) return null;
    if (
      !vehicleCapacityContext.demandByDay ||
      !vehicleCapacityContext.rangeStart ||
      !vehicleCapacityContext.rangeEnd
    ) {
      return null;
    }
    const over = isVehicleTypeSelectionOverCapacity(
      formData.assignedVehicleTypes,
      vehicleCapacityContext.capacityByType,
      vehicleCapacityContext.demandByDay,
      vehicleCapacityContext.rangeStart,
      vehicleCapacityContext.rangeEnd
    );
    return over ? msg : null;
  }, [
    formData.assignedVehicleTypes,
    formData.foremanArea,
    formData.identifiedDate,
    formData.plannedCompletionDate,
    scheduleResourcesForAvailability,
    loadingScheduleAvailability,
    equipmentAvailabilityFetchFailed,
    availableVehicles,
    selectedPlanServiceAreaId,
    vehicleCapacityContext,
    task?.id,
  ]);

  const dependencyResourceOptions = useMemo(() => {
    const set = new Set<string>();
    availableVehicles.forEach((v) => {
      const t = (v.vehicleType || 'Unspecified').trim() || 'Unspecified';
      set.add(t);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [availableVehicles]);

  // Field locking logic based on task state and user permissions
  const canEditInternalReviewPermission = canEditInternalReview(hasPermission);
  
  const fieldLockingState = getFieldLockingState({
    taskStatus: task?.status as TaskStage,
    hasEditInternalReviewPermission: canEditInternalReviewPermission
  });
  
  const isTaskLocked = fieldLockingState.isLocked;
  const lockedFieldStyle = getLockedFieldStyle(isTaskLocked);
  
  // Legacy variable for backward compatibility
  const isTaskClosed = task?.status === TaskStage.CLOSED;

  // Auto-show map for closed/finalized tasks with GPS coordinates
  useEffect(() => {
    if (task) {
      const isTaskClosedOrFinalized = task.status === TaskStage.CLOSED || task.status === TaskStage.FINALIZED;
      const startLocation = task.taskLocations?.find(loc => loc.type === TaskLocationType.START);
      const endLocation = task.taskLocations?.find(loc => loc.type === TaskLocationType.END);
      const hasGPS = (startLocation?.latitude && startLocation?.longitude) || 
                     (endLocation?.latitude && endLocation?.longitude);
      
      if (isTaskClosedOrFinalized && hasGPS) {
        setShowMap(true);
      }
    }
  }, [task]);

  useEffect(() => {
    if (task) {
      
      // Get task locations
      const startLocation = task.taskLocations?.find(loc => loc.type === TaskLocationType.START);
      const endLocation = task.taskLocations?.find(loc => loc.type === TaskLocationType.END);
      
      // Get task quantities and rates
      const identifiedQuantity = task.quantifiedTasks?.[0]?.identifiedQuantity || 0;
      const baseRate = task.taskCostings?.[0]?.baseRate || 0;
      const rateMultiplier = task.taskCostings?.[0]?.rateMultiplier || 1;
      const rateAdjustment = task.taskCostings?.[0]?.rateAdjustment || 0;
      const parsedDependencies = extractScheduleDependenciesFromComments(task.comments || null);
      
      // Get the asset type from the start location's asset
      const assetType = startLocation?.asset?.assetType?.toLowerCase() as AssetTypeEnum | undefined;
      
      // Update form data if it's a different task OR if key fields have changed
      // This ensures updates (like completionAccomplishment, completionDate) are reflected
      const taskCompletionAccomplishment = task.completionAccomplishment?.toString() || '';
      const taskCompletionDate = task.completionDate ? new Date(task.completionDate).toISOString().split('T')[0] : '';
      const taskStartDate = (() => {
        if (task.startDate) {
          return new Date(task.startDate).toISOString().split('T')[0];
        }
        const earliestEffortDate = getEarliestEffortDate(task);
        if (earliestEffortDate) {
          return earliestEffortDate.toISOString().split('T')[0];
        }
        return task.identifiedDate ? new Date(task.identifiedDate).toISOString().split('T')[0] : '';
      })();
      
      // Check if Work Planning fields have changed
      const taskEstimatedEffortHours = task.estimatedEffortHours?.toString() || '';
      const taskPlannedCompletionDate = task.plannedCompletionDate ? new Date(task.plannedCompletionDate).toISOString().split('T')[0] : '';
      const taskAssignedEmployeeIds = task.assignedEmployees?.map(emp => emp.id) || [];
      
      const currentAssignedIdsStr = JSON.stringify(formData.assignedEmployeeIds || []);
      const taskAssignedIdsStr = JSON.stringify(taskAssignedEmployeeIds);
      
      // Don't update if this is the same task and Work Planning fields haven't changed
      // This prevents unnecessary resets when task is refreshed after save
      // (Assigned equipment types are synced in a separate effect when schedule vehicles load.)
      const isSameTaskId = formData.id === task.id;
      const workPlanningChanged = 
        formData.estimatedEffortHours !== taskEstimatedEffortHours ||
        formData.plannedCompletionDate !== taskPlannedCompletionDate ||
        currentAssignedIdsStr !== taskAssignedIdsStr;
      
      const shouldUpdate = !isSameTaskId || 
        formData.completionAccomplishment !== taskCompletionAccomplishment ||
        formData.completionDate !== taskCompletionDate ||
        formData.completionStartDate !== taskStartDate ||
        formData.status !== (task.status as TaskStage) ||
        workPlanningChanged;
      
      
      if (shouldUpdate) {
        vehicleTypesSyncedKeyRef.current = '';
        setFormData({
          ...initialFormState,
          id: task.id,
          activityId: task.activities?.[0]?.id,
          description: task.description || '',
          instructions: task.instructions || '',
          name: task.name || '',
          priority: task.priority || 'LOW',
          status: task.status as TaskStage,
          taskType: task.taskCostings?.[0]?.taskType || 'Unit Rate',
          costType: task.taskCostings?.[0]?.costType || 'Fixed Cost',
          rateAdjustmentComments: task.taskCostings?.[0]?.rateAdjustmentComments || '',
          labourMarkup: task.taskCostings?.[0]?.labourMarkup || 0,
          materialMarkup: task.taskCostings?.[0]?.materialMarkup || 0,
          subcontractorMarkup: task.taskCostings?.[0]?.subcontractorMarkup || 0,
          vehicleMarkup: task.taskCostings?.[0]?.vehicleMarkup || 0,
          hiredEquipmentMarkup: task.taskCostings?.[0]?.hiredEquipmentMarkup || 0,
          project: task.projectId?.toString() || '',
          foremanArea: task.foremanAreaId?.toString() || '',
          contractArea: task.contractAreaId?.toString() || '',
          contractYear: task.contractYear || '',
          identifiedDate: task.identifiedDate ? new Date(task.identifiedDate).toISOString().split('T')[0] : '',
          completionDate: taskCompletionDate,
          completionStartDate: taskStartDate,
          completionAccomplishment: taskCompletionAccomplishment,
          identifiedQuantity: identifiedQuantity.toString(),
          unitOfMeasure: task.quantifiedTasks?.[0]?.unitOfMeasure || '',
          baseRate: baseRate.toString(),
          rateMultiplier: rateMultiplier,
          rateAdjustment: rateAdjustment.toString(),
          total: task.taskCostings?.[0]?.totalValue?.toString() || '',
          startLatitude: startLocation?.latitude?.toString() || '',
          startLongitude: startLocation?.longitude?.toString() || '',
          endLatitude: endLocation?.latitude?.toString() || '',
          endLongitude: endLocation?.longitude?.toString() || '',
          assetId: startLocation?.assetId || null,
          assetType: assetType,
          comments: parsedDependencies.cleanComments || '',
          taskNumber: task.taskNumber || '',
          estimatedEffortHours: task.estimatedEffortHours?.toString() || '',
          plannedCompletionDate: task.plannedCompletionDate ? new Date(task.plannedCompletionDate).toISOString().split('T')[0] : '',
          assignedEmployeeIds: task.assignedEmployees?.map(emp => emp.id) || [],
          assignedVehicleTypes: [],
          dependencyTriggers: parsedDependencies.dependencies?.triggers || [],
          dependencyOtherComment: parsedDependencies.dependencies?.otherComment || '',
          dependencyResourceType: parsedDependencies.dependencies?.resourceDependentType || '',
          dependencyCompletionTaskId: parsedDependencies.dependencies?.completionTaskId ? String(parsedDependencies.dependencies.completionTaskId) : '',
        });
        

        // If there's a contract area ID, load the contract areas for the foreman area
        if (task.foremanAreaId && task.contractAreaId) {
          // fetchContractAreas(task.foremanAreaId); // Commented out to show all contract areas
          
          // If we have an asset type, load the assets for the contract area
          if (assetType) {
            loadAssets(task.contractAreaId, assetType);
          }
        }
      }
    }
  }, [task]); // Remove fetchContractAreas from dependencies since it's defined later

  // Fetch active employees for selection, filtered by selected service area
  useEffect(() => {
    const fetchEmployees = async () => {
      if (visible && task?.mode?.toLowerCase() !== 'standing') {
        try {
          setLoadingEmployees(true);
          const foremanAreaId = formData.foremanArea ? Number(formData.foremanArea) : undefined;
          const serviceAreaId = foremanAreaId
            ? foremanAreas.find((area) => area.id === foremanAreaId)?.serviceAreaId
            : undefined;
          const activeEmployees = await employeeService.getActiveEmployeesForSelection(
            undefined,
            serviceAreaId,
            true
          );
          setEmployees(activeEmployees);
        } catch (error) {
          console.error('Error fetching employees:', error);
        } finally {
          setLoadingEmployees(false);
        }
      }
    };
    fetchEmployees();
  }, [visible, task?.mode, formData.foremanArea, foremanAreas]);

  useEffect(() => {
    let cancelled = false;
    const loadDependencyTasks = async () => {
      if (!visible || task?.mode?.toLowerCase() === 'standing') return;
      const selectedForemanAreaId = formData.foremanArea ? Number(formData.foremanArea) : NaN;
      if (!Number.isFinite(selectedForemanAreaId) || selectedForemanAreaId <= 0) {
        if (!cancelled) setDependencyTaskOptions([]);
        return;
      }
      try {
        const response = await taskService.getQuantifiedTasksPaginated({
          page: 1,
          limit: 500,
          status: ['PROPOSED', 'PLANNED', 'ACTIVE', 'ACTIVE_MARKED_AS_COMPLETE'],
          foremanArea: String(selectedForemanAreaId),
        });
        const rows = Array.isArray(response?.data) ? response.data : [];
        const mapped = rows
          .filter((t: { id?: number; foremanAreaId?: number | null }) => !task?.id || t.id !== task.id)
          .map((t: { id: number; taskNumber?: string | null; name?: string | null; description?: string | null }) => ({
            id: Number(t.id),
            label: `${t.taskNumber || `#${t.id}`} - ${t.name || t.description || 'Task'}`,
          }));
        if (!cancelled) setDependencyTaskOptions(mapped);
      } catch (err) {
        console.error('Failed to load dependency task options:', err);
        if (!cancelled) setDependencyTaskOptions([]);
      }
    };
    void loadDependencyTasks();
    return () => {
      cancelled = true;
    };
  }, [visible, task?.mode, task?.id, formData.foremanArea]);

  // Fetch available vehicles for planned assignment (filtered by foreman area + its service area)
  useEffect(() => {
    const fetchVehicles = async () => {
      if (!visible || task?.mode?.toLowerCase() === 'standing') return;
      if (!formData.foremanArea) return;

      const selectedForemanArea = foremanAreas.find((area) => area.id === Number(formData.foremanArea));
      const serviceAreaId = selectedForemanArea?.serviceAreaId;

      const queryParams: { serviceArea?: string; foremanArea?: string } = {};
      if (serviceAreaId) queryParams.serviceArea = String(serviceAreaId);
      queryParams.foremanArea = String(formData.foremanArea);

      try {
        setLoadingVehicles(true);
        const vehicles = await getScheduleVehicles(queryParams);
        setAvailableVehicles(Array.isArray(vehicles) ? vehicles : []);
      } catch (error) {
        console.error('Error fetching vehicles for planning:', error);
        setAvailableVehicles([]);
      } finally {
        setLoadingVehicles(false);
      }
    };

    fetchVehicles();
  }, [visible, task?.mode, formData.foremanArea, foremanAreas]);

  useEffect(() => {
    if (!visible) {
      vehicleTypesSyncedKeyRef.current = '';
    }
  }, [visible]);

  // Load schedule resources for equipment capacity vs demand (same filters as schedule)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!visible || task?.mode?.toLowerCase() === 'standing') {
        setScheduleResourcesForAvailability(null);
        setEquipmentAvailabilityFetchFailed(false);
        return;
      }
      if (!formData.foremanArea || !formData.identifiedDate || !formData.plannedCompletionDate) {
        setScheduleResourcesForAvailability(null);
        setEquipmentAvailabilityFetchFailed(false);
        return;
      }
      const faId = Number(formData.foremanArea);
      const selectedForemanArea = foremanAreas.find((area) => area.id === faId);
      const serviceAreaId = selectedForemanArea?.serviceAreaId;
      try {
        setLoadingScheduleAvailability(true);
        setEquipmentAvailabilityFetchFailed(false);
        const resources = await getScheduleResources({
          dateFrom: formData.identifiedDate,
          dateTo: formData.plannedCompletionDate,
          serviceArea: serviceAreaId != null ? String(serviceAreaId) : undefined,
          foremanArea: String(faId),
          includeUnassignedVehicleTasks: true,
        });
        if (!cancelled) {
          setScheduleResourcesForAvailability(Array.isArray(resources) ? resources : []);
        }
      } catch (error) {
        console.error('Error loading schedule for equipment availability:', error);
        if (!cancelled) {
          setScheduleResourcesForAvailability(null);
          setEquipmentAvailabilityFetchFailed(true);
        }
      } finally {
        if (!cancelled) setLoadingScheduleAvailability(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [
    visible,
    task?.mode,
    formData.foremanArea,
    formData.identifiedDate,
    formData.plannedCompletionDate,
    foremanAreas,
  ]);

  // After vehicles load, infer equipment types from saved task unit numbers (once per task snapshot)
  useEffect(() => {
    if (!visible || !task?.id || loadingVehicles || !availableVehicles.length) return;
    const units =
      task.vehicles?.map((v) => v.unitUsed).filter((u): u is string => Boolean(u && String(u).trim())) || [];
    const key = `${task.id}:${[...units].sort().join('|')}`;
    if (vehicleTypesSyncedKeyRef.current === key) return;
    const types = inferUniqueTypesFromUnits(units, availableVehicles);
    vehicleTypesSyncedKeyRef.current = key;
    setFormData((prev) => (prev.id !== task.id ? prev : { ...prev, assignedVehicleTypes: types }));
  }, [visible, task?.id, task?.vehicles, availableVehicles, loadingVehicles]);

  // Debug: Log formData.assignedEmployeeIds changes
  useEffect(() => {
    if (formData.assignedEmployeeIds && formData.assignedEmployeeIds.length > 0) {
      const matchingEmployees = employees.filter(emp => formData.assignedEmployeeIds?.includes(emp.id));
    }
  }, [formData.assignedEmployeeIds, employees]);

  // Track taskHistory changes to ensure components that depend on it re-render
  // This is important when task history is updated (e.g., from actual accomplishment updates)
  useEffect(() => {
    if (task?.taskHistory) {
      // Force a re-render by updating a state that components might depend on
      // The task prop change should already trigger re-renders, but this ensures
      // that any memoized values or derived state also update
    }
  }, [task?.taskHistory?.length, task?.taskHistory?.[0]?.id]);

  useEffect(() => {
    // The modal is mounted unconditionally by its parents and only bails out at render time
    // (`if (!visible) return null` near the bottom of this file), which is AFTER every hook has
    // run. Without this guard the request below fired for an invisible modal - and because
    // QPTracking remounted its whole subtree on each list fetch, it fired again on every single
    // list request. The sibling effects here already guard on `visible`; these two did not.
    if (!visible) return;

    let cancelled = false;

    const loadActivities = async () => {
      try {
        const filters: ActivityQueryParams = {};

        const contractYearNumber = Number(formData.contractYear);
        if (!Number.isNaN(contractYearNumber) && contractYearNumber > 0) {
          filters.contractYear = contractYearNumber;
        }

        if (formData.foremanArea) {
          const selectedForemanArea = foremanAreas.find(
            (area) => area.id === Number(formData.foremanArea)
          );
          if (selectedForemanArea?.serviceAreaId) {
            filters.serviceAreaId = selectedForemanArea.serviceAreaId;
          }
        }

        const hasFilters = Object.keys(filters).length > 0;
        let fetchedActivities = await activityService.getAllActivities(
          hasFilters ? filters : undefined
        );

        if (cancelled) return;

        if (
          Array.isArray(fetchedActivities) &&
          fetchedActivities.length === 0 &&
          filters.contractYear
        ) {
          fetchedActivities = await activityService.getAllActivities({
            serviceAreaId: filters.serviceAreaId,
          });
        }

        if (cancelled) return;

        if (!Array.isArray(fetchedActivities)) {
          console.warn('ActivityService returned non-array response:', fetchedActivities);
          setActivities([]);
          return;
        }

        setActivities(fetchedActivities);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading activities:', error);
          setActivities([]);
        }
      }
    };

    loadActivities();
    return () => { cancelled = true; };
  }, [visible, formData.contractYear, formData.foremanArea, foremanAreas]);

  useEffect(() => {
    // Same guard as the activities effect above, and for the same reason: an invisible modal
    // should not load (or report a failure for) data nobody is looking at.
    if (!visible) return;

    // `cancelled` was missing here, unlike the activities effect next door: overlapping
    // foremanArea changes could resolve out of order and land the older project list in state.
    let cancelled = false;

    const loadProjects = async () => {
      try {

        // Get the service area from the selected foreman area for filtering
        let serviceAreaId: string | undefined;
        if (formData.foremanArea) {
          const selectedForemanArea = foremanAreas.find(area => area.id === Number(formData.foremanArea));
          if (selectedForemanArea?.serviceAreaId) {
            serviceAreaId = selectedForemanArea.serviceAreaId.toString();
          }
        }

        // Use the new endpoint with service area filter and only active/planned projects
        const fetchedProjects = await projectService.getProjectsForTaskModal(
          serviceAreaId,
          'active,planned'
        );

        if (cancelled) return;

        // Ensure fetchedProjects is an array
        if (!Array.isArray(fetchedProjects)) {
          console.warn("ProjectService returned non-array response:", fetchedProjects);
          setProjects([]);
          return;
        }

        setProjects(fetchedProjects);
        setProjectsLoadError(false);
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading projects:', error);
        setProjectsLoadError(true);
        setProjects([]);
      }
    };
    loadProjects();
    return () => { cancelled = true; };
  }, [visible, formData.foremanArea, foremanAreas, projectsReloadKey]);

  useEffect(() => {
    if (!formData.contractArea || !formData.assetType) {
      setAssets([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      loadAssets(Number(formData.contractArea), formData.assetType);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formData.contractArea, formData.assetType]);

  // Update useEffect for loading road assets
  useEffect(() => {
    if (!formData.contractArea) {
      return;
    }

    // Only load road assets initially if viewing road assets or editing an existing task
    if (formData.assetType?.toLowerCase() === 'road' || task?.id) {
      loadRoadAssets(Number(formData.contractArea));
    }
  }, [formData.contractArea, formData.assetType, task?.id]);

  // Add debugging useEffect for asset selection
  useEffect(() => {
    if (formData.assetId) {
    }
  }, [formData.assetId]);

  useEffect(() => {
    if (task?.id) {
      loadAttachments();
    }
  }, [task]);

  const calculateRate = (baseRate: string | number, multiplier: string | number, adjustment: string | number): number => {
    const base = Number(baseRate) || 0;
    const mult = Number(multiplier) || 1;
    const adj = Number(adjustment) || 0;
    
    // If there's a rate adjustment, use it instead of base rate
    const effectiveRate = adj > 0 ? adj : base;
    return effectiveRate * mult;
  };

  const calculateTotal = (rate: string | number, quantity: string | number): number => {
    const rateNum = Number(rate) || 0;
    const quantityNum = Number(quantity) || 0;
    return rateNum * quantityNum;
  };

  // Add this useEffect to handle calculations
  useEffect(() => {
    if (formData.taskType === 'Unit Rate') {
      const baseRate = Number(formData.baseRate) || 0;
      const multiplier = Number(formData.rateMultiplier) || 1;
      const adjustment = Number(formData.rateAdjustment) || 0;
      const quantity = Number(formData.identifiedQuantity) || 0;

      // Calculate rate - use adjustment instead of base rate if it's greater than 0
      const effectiveRate = adjustment > 0 ? adjustment : baseRate;
      const calculatedRate = effectiveRate * multiplier;
      
      // Calculate total
      const total = calculatedRate * quantity;

      // Update form data with new calculations
      setFormData(prev => ({
        ...prev,
        calculatedRate: calculatedRate.toFixed(2),
        total: total.toFixed(2)
      }));
    }
  }, [
    formData.baseRate,
    formData.rateMultiplier,
    formData.rateAdjustment,
    formData.identifiedQuantity,
    formData.taskType
  ]);

  // Update the handleChange function to handle text inputs
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'name') {
      isTaskNameManuallyEditedRef.current = String(value).trim().length > 0;
      setFormData(prev => ({ ...prev, name: value }));
      return;
    }
    
    // Special handling for actual accomplishment fields
    if (name === 'completionAccomplishment' || name === 'completionDate' || name === 'completionStartDate') {
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // For Unit Rate tasks, clear actualTotalValueInput when completionAccomplishment changes
      // so the calculated value shows in real-time
      if (name === 'completionAccomplishment') {
        const taskCosting = task?.taskCostings?.[0];
        if (taskCosting?.taskType === 'Unit Rate') {
          setActualTotalValueInput(undefined);
          setIsActualTotalValueManuallyEdited(false);
        }
      }
      return;
    }
    
    // Special handling for identifiedQuantity to prevent negative numbers
    if (name === 'identifiedQuantity') {
      const numValue = Number(value);
      if (numValue < 0) return; // Don't update if negative
    }
    
    // Handle asset type changes by clearing the asset ID
    if (name === 'assetType') {
      setFormData(prev => {
        const newFormData = { ...prev };
        newFormData.assetType = value as AssetTypeEnum;
        newFormData.assetId = null;
        return newFormData;
      });
      return;
    }
    
    
    // Handle asset selection
    if (name === 'assetId') {
      const assetId = value ? Number(value) : null;
      const selectedAsset = assets.find(a => a.id === assetId);
      const selectedActivity = activities.find(a => a.id === formData.activityId);

      setFormData(prev => {
        const shouldAutoGenerateName = !isTaskNameManuallyEditedRef.current || !String(prev.name || '').trim();
        const generatedName = selectedActivity && shouldAutoGenerateName
          ? generateTaskName(selectedActivity, selectedAsset)
          : prev.name;

        return {
          ...prev,
          assetId,
          name: generatedName
        };
      });
      return;
    }
    
    // Planned work planning: completion must be >= start; block invalid picks (also use min/max on inputs).
    if (name === 'identifiedDate' || name === 'plannedCompletionDate') {
      const nextStart = (name === 'identifiedDate' ? value : formData.identifiedDate || '').trim();
      const nextEnd = (name === 'plannedCompletionDate' ? value : formData.plannedCompletionDate || '').trim();
      if (nextStart && nextEnd && nextEnd < nextStart) {
        if (name === 'plannedCompletionDate') {
          raiseFieldErrors({ plannedCompletionDate: "Planned completion date can't be before the start date" }, false);
        } else {
          raiseFieldErrors({ identifiedDate: "Planned start date can't be after the completion date" }, false);
        }
        return;
      }
      setSaveError(null);
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    // Set identifiedDate to current date when status is set to Active
    if (name === 'status' && value === TaskStage.ACTIVE) {
      const today = getCurrentLocalDateString();
      setFormData((prev) => {
        let plannedCompletionDate = prev.plannedCompletionDate;
        const c = String(plannedCompletionDate || '').trim();
        if (c && c < today) {
          plannedCompletionDate = today;
        }
        return {
          ...prev,
          status: value,
          identifiedDate: today,
          plannedCompletionDate,
        };
      });
      return;
    }
    
    // Handle numeric inputs that need calculation
    if (['baseRate', 'rateAdjustment', 'rateMultiplier', 'identifiedQuantity'].includes(name)) {
      // Allow free text input for these fields
      let processedValue = value;
      
      // Strip currency symbols if present
      if (typeof value === 'string' && value.includes('$')) {
        processedValue = stripCurrency(value);
      }
      
      setFormData(prev => {
        const updated = { ...prev, [name]: processedValue };
        
        // Only recalculate if in Unit Rate mode
        if (prev.taskType === 'Unit Rate') {
          const baseRate = Number(updated.baseRate) || 0;
          const multiplier = Number(updated.rateMultiplier) || 1;
          const adjustment = Number(updated.rateAdjustment) || 0;
          const quantity = Number(updated.identifiedQuantity) || 0;
          
          // Use adjustment instead of base rate if it's greater than 0
          const effectiveRate = adjustment > 0 ? adjustment : baseRate;
          const calculatedRate = effectiveRate * multiplier;
          const total = calculatedRate * quantity;
          
          return {
            ...updated,
            calculatedRate: calculatedRate.toFixed(2),
            total: total.toFixed(2)
          };
        }
        
        return updated;
      });

      // Rate/costing inputs affect actual total value; clear stale persisted value so save recalculates
      if (
        ['baseRate', 'rateAdjustment', 'rateMultiplier'].includes(name) &&
        formData.taskType === 'Unit Rate' &&
        allowsActualTotalValueOverride(formData.status)
      ) {
        setActualTotalValueInput(undefined);
        setIsActualTotalValueManuallyEdited(false);
      }
      return;
    }
    
    // Handle markup percentages
    if (['labourMarkup', 'materialMarkup', 'subcontractorMarkup', 'vehicleMarkup', 'hiredEquipmentMarkup'].includes(name)) {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      return;
    }
    
    // Handle total field for Lump Sum separately - allow free text input
    if (name === 'total') {
      // Keep the value as is, even if it has currency symbols
      setFormData(prev => ({
        ...prev,
        total: value
      }));
      return;
    }
    
    // Special handling for cost type changes
    if (name === 'costType') {
      const newCostType = value as 'Fixed Cost' | 'Cost Plus';
      if (newCostType === 'Cost Plus') {
        const selectedForemanArea = foremanAreas.find(area => area.id === Number(formData.foremanArea));
        const serviceAreaId = selectedForemanArea?.serviceArea?.id.toString();
        const defaultMarkups = serviceAreaId ? SERVICE_AREA_MARKUPS[serviceAreaId as keyof typeof SERVICE_AREA_MARKUPS] : null;

        setFormData(prev => ({
          ...prev,
          costType: newCostType,
          ...(defaultMarkups && {
            labourMarkup: defaultMarkups.labourMarkup,
            materialMarkup: defaultMarkups.materialMarkup,
            vehicleMarkup: defaultMarkups.vehicleMarkup,
            subcontractorMarkup: defaultMarkups.subcontractorMarkup,
            hiredEquipmentMarkup: defaultMarkups.hiredEquipmentMarkup
          })
        }));
        return;
      } else {
        // Reset markups to 0 when switching to Fixed Cost
        setFormData(prev => ({
          ...prev,
          costType: newCostType,
          labourMarkup: 0,
          materialMarkup: 0,
          vehicleMarkup: 0,
          subcontractorMarkup: 0,
          hiredEquipmentMarkup: 0
        }));
        return;
      }
    }
    

    if (name === 'contractYear') {
      const raw = String(value ?? '').trim();
      contractYearDefaultAppliedRef.current = true;
      setSaveError(null);
      setFormData((prev) => ({ ...prev, contractYear: raw }));
      return;
    }

    if (name === 'identifiedDate') {
      setFormData((prev) => {
        const next: FormData = { ...prev, identifiedDate: value };
        if (
          value &&
          prev.plannedCompletionDate &&
          value > prev.plannedCompletionDate
        ) {
          next.plannedCompletionDate = value;
        }
        return next;
      });
      return;
    }

    if (name === 'plannedCompletionDate') {
      setFormData((prev) => {
        let planned = value;
        if (planned && prev.identifiedDate && planned < prev.identifiedDate) {
          planned = prev.identifiedDate;
        }
        return { ...prev, plannedCompletionDate: planned };
      });
      return;
    }

    // Default handling for other fields
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Update the handleActivityChange function
  const handleActivityChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const activityId = Number(e.target.value);
    const selectedActivity = activities.find(a => a.id === activityId);
    
    if (selectedActivity) {
      const selectedAsset = assets.find(a => a.id === formData.assetId);
      const generatedName = generateTaskName(selectedActivity, selectedAsset);
      
      setFormData(prev => ({
        ...prev,
        activityId,
        name: (!isTaskNameManuallyEditedRef.current || !String(prev.name || '').trim()) ? generatedName : prev.name,
        unitOfMeasure: selectedActivity.unitOfMeasure,
        baseRate: selectedActivity.scheduled2Rate?.toString() || '0',
        // Reset rate-related fields to defaults
        rateMultiplier: 1,
        rateAdjustment: '',
        calculatedRate: selectedActivity.scheduled2Rate?.toString() || '0',
        total: '0'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        activityId
      }));
    }
  };

  // Add this new function to handle activity selection from SearchableDropdown
  const handleActivitySelection = (value: string) => {
    handleActivityChange({ target: { value } } as React.ChangeEvent<HTMLSelectElement>);
  };

  const handleTaskTypeChange = (value: string) => {
    setFormData(prev => {
      const selectedActivity = activities.find(a => a.id === prev.activityId);
      const updates: Partial<FormData> = {
        taskType: value as 'Unit Rate' | 'Lump Sum',
        costType: 'Fixed Cost', // Reset to default when changing task type
      };

      if (value === 'Lump Sum') {
        // For Lump Sum, clear rate-related fields
        updates.baseRate = '';
        updates.rateMultiplier = 1;
        updates.rateAdjustment = '';
        updates.calculatedRate = '';
        updates.total = '0';
        updates.labourMarkup = 0;
        updates.materialMarkup = 0;
        updates.subcontractorMarkup = 0;
        updates.vehicleMarkup = 0;
        updates.hiredEquipmentMarkup = 0;
        updates.rateAdjustmentComments = '';
      } else {
        // For Unit Rate, restore base rate from activity
        updates.baseRate = selectedActivity?.scheduled2Rate?.toString() || '0';
        // Recalculate rate and total
        const baseRate = Number(updates.baseRate) || 0;
        const adjustment = Number(prev.rateAdjustment) || 0;
        const multiplier = Number(prev.rateMultiplier) || 1;
        
        // Use adjustment instead of base rate if it's greater than 0
        const effectiveRate = adjustment > 0 ? adjustment : baseRate;
        const calculatedRate = effectiveRate * multiplier;
        
        updates.calculatedRate = calculatedRate.toString();
        updates.total = calculateTotal(
          calculatedRate,
          prev.identifiedQuantity || 0
        ).toString();
      }

      return { ...prev, ...updates };
    });
  };

  /** The value a field's error is about; when it changes, the error is cleared. */
  const fieldErrorValue = (key: TaskFieldKey, fd: FormData): string => {
    switch (key) {
      case 'startLocation':
        return `${fd.startLatitude ?? ''}|${fd.startLongitude ?? ''}`;
      case 'identifiedDate':
      case 'plannedCompletionDate':
        return `${fd.identifiedDate ?? ''}|${fd.plannedCompletionDate ?? ''}|${fd.contractYear ?? ''}`;
      case 'assignedVehicleTypes':
        return JSON.stringify(fd.assignedVehicleTypes ?? []);
      case 'dependencyOtherComment':
        return `${fd.dependencyOtherComment ?? ''}|${JSON.stringify(fd.dependencyTriggers ?? [])}`;
      default:
        return String((fd as any)[key] ?? '');
    }
  };

  /**
   * Shows errors under their fields. With `moveFocus`, switches to the tab holding the first error
   * (the current tab first, if it has one) and focuses that field (FORM-01).
   */
  const raiseFieldErrors = (errors: TaskFieldErrors, moveFocus = true) => {
    const keys = TASK_FIELD_ORDER.filter((k) => errors[k]);
    for (const k of keys) fieldErrorSnapshotRef.current[k] = fieldErrorValue(k, formData);
    setFieldErrors((prev) => ({ ...prev, ...errors }));
    if (!moveFocus || keys.length === 0) return;
    const first = keys.find((k) => TASK_FIELD_TAB[k] === activeTab) ?? keys[0];
    setActiveTab(TASK_FIELD_TAB[first]);
    setTimeout(() => focusTaskField(first), 0);
  };

  // Editing a field clears its error.
  useEffect(() => {
    setFieldErrors((prev) => {
      let changed = false;
      const next: TaskFieldErrors = { ...prev };
      (Object.keys(prev) as TaskFieldKey[]).forEach((k) => {
        if (prev[k] && fieldErrorSnapshotRef.current[k] !== fieldErrorValue(k, formData)) {
          delete next[k];
          delete fieldErrorSnapshotRef.current[k];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // fieldErrorValue only reads its arguments.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const tabErrorCount = (tab: TabType) =>
    (Object.keys(fieldErrors) as TaskFieldKey[]).filter((k) => fieldErrors[k] && TASK_FIELD_TAB[k] === tab).length;

  const tabLabel = (label: string, tab: TabType) => {
    const count = tabErrorCount(tab);
    return count > 0 ? (
      <>
        {label} <span className="tw-text-danger">({count})</span>
      </>
    ) : (
      label
    );
  };

  /** Renders a field's error under it (FORM-01). */
  const renderFieldError = (key: TaskFieldKey) =>
    fieldErrors[key] ? (
      <Form.Control.Feedback type="invalid" className="tw-block" id={`${taskFieldId(key)}-error`}>
        {fieldErrors[key]}
      </Form.Control.Feedback>
    ) : null;

  /** Collects every field error; returns true when the form can be saved. */
  const validateForm = (): boolean => {
    const errors: TaskFieldErrors = {};
    if (!formData.activityId) {
      errors.activityId = 'Activity required';
    }
    if (!formData.foremanArea) {
      errors.foremanArea = 'Foreman area required';
    }
    if (!formData.contractYear) {
      errors.contractYear = 'Contract year required';
    }
    const plannedStartCheck = (formData.identifiedDate || '').trim();
    const plannedEndCheck = (formData.plannedCompletionDate || '').trim();
    if (plannedStartCheck && plannedEndCheck && plannedEndCheck < plannedStartCheck) {
      errors.plannedCompletionDate = "Planned completion date can't be before the start date";
    }
    const isWorkPlanningRequired = formData.status === TaskStage.PLANNED || formData.status === TaskStage.ACTIVE;
    if (isWorkPlanningRequired) {
      if (!formData.identifiedDate) {
        errors.identifiedDate = 'Planned start date required';
      }
      if (!formData.plannedCompletionDate) {
        errors.plannedCompletionDate = 'Planned completion date required';
      }
      if (contractYearDateMismatch?.isOpenPlan && !errors.identifiedDate) {
        errors.identifiedDate = formatContractYearDateMismatchMessage(contractYearDateMismatch);
      }
      if (formData.assignedVehicleTypes && formData.assignedVehicleTypes.length > 0) {
        if (loadingScheduleAvailability) {
          errors.assignedVehicleTypes = 'Equipment availability is still loading';
        } else if (equipmentAvailabilityFetchFailed) {
          errors.assignedVehicleTypes = "Couldn't check equipment availability. Try again.";
        } else if (equipmentAvailabilityMessage) {
          errors.assignedVehicleTypes = equipmentAvailabilityMessage;
        }
      }
      if (
        Array.isArray(formData.dependencyTriggers) &&
        formData.dependencyTriggers.includes('Other') &&
        !String(formData.dependencyOtherComment || '').trim()
      ) {
        errors.dependencyOtherComment = 'Comment required for the "Other" dependency';
      }
    }
    if (!formData.name) {
      errors.name = 'Task name required';
    }
    if (!formData.identifiedQuantity) {
      errors.identifiedQuantity = 'Planned quantity required';
    } else if (Number(formData.identifiedQuantity) <= 0) {
      errors.identifiedQuantity = 'Planned quantity must be more than 0';
    }
    if (!formData.unitOfMeasure) {
      errors.unitOfMeasure = 'Unit of measure required';
    }

    // Validate based on task type
    if (formData.taskType === 'Unit Rate') {
      if (!formData.baseRate) {
        errors.baseRate = 'Base rate required';
      } else if (Number(formData.baseRate) < 0) {
        errors.baseRate = "Base rate can't be negative";
      }
    } else if (formData.taskType === 'Lump Sum' && formData.costType === 'Fixed Cost') {
      // For Lump Sum Fixed Cost, validate the total field
      if (!formData.total) {
        errors.total = 'Total expected value required';
      } else if (Number(stripCurrency(formData.total.toString())) <= 0) {
        errors.total = 'Total expected value must be more than 0';
      }
    }

    if (!formData.startLatitude || !formData.startLongitude) {
      errors.startLocation = 'Starting location required';
    }

    if (Object.keys(errors).length > 0) {
      raiseFieldErrors(errors);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaveError(null);
    setLoading(true);
    
    try {
      // If on Actual Accomplishment tab and can edit, update actual accomplishment first
      if (
        activeTab === 'actual_accomplishment' &&
        task?.id &&
        hasPermission('quantified:actual_accomplishment_edit') &&
        task.status === TaskStage.INTERNAL_REVIEW
      ) {
        const result = await updateActualAccomplishmentIfNeeded();
        if (result) {
          // No need to proceed with full task update, as actual accomplishment is a separate API call
          setLoading(false);
          onClose();
          return;
        } else {
          setLoading(false);
          return;
        }
      }
      
      const formDataToSubmit = getFormData();
      
      let savedTask: Task;
      let otherCostsSaved = true;
      if (task) {
        // Update existing task
        savedTask = await taskService.updateFullTask(task.id.toString(), formDataToSubmit);
        
        // Save other costs as efforts only if the user opened the Other Costs tab (otherwise we would overwrite with empty list)
        if (hasPermission('quantified:other_costs_manage') && otherCostsLoadedTaskId === task?.id) {
          otherCostsSaved = await saveOtherCostsAsEfforts(savedTask.id);
        }
        
        // Refresh the task to get the latest history and all related data
        // This ensures all tabs see the updated task history
        try {
          const refreshedTask = await taskService.getTask(savedTask.id);
          savedTask = refreshedTask;
        } catch (error) {
          console.warn('Failed to refresh task after update, using saved task:', error);
          // Continue with savedTask if refresh fails
        }
      } else {
        // Create new task
        const resolvedProjectId = formData.project ? Number(formData.project) : NaN;
        if (!Number.isNaN(resolvedProjectId)) {
          const projMeta =
            projects.find(p => p.id === resolvedProjectId) ??
            selectedProject ??
            (initialProject?.id === resolvedProjectId ? initialProject : undefined);
          if (projMeta?.status?.toLowerCase() === 'completed') {
            raiseFieldErrors({ project: 'Project is completed. Re-open it first, or choose another project.' });
            setLoading(false);
            return;
          }
        }
        savedTask = await taskService.createTask(formDataToSubmit);
        
        // Save other costs as efforts for new tasks
        if (otherCosts.length > 0 && hasPermission('quantified:other_costs_manage')) {
          otherCostsSaved = await saveOtherCostsAsEfforts(savedTask.id);
        }
        
        // Refresh the newly created task to get the latest history
        try {
          const refreshedTask = await taskService.getTask(savedTask.id);
          savedTask = refreshedTask;
        } catch (error) {
          console.warn('Failed to refresh newly created task, using saved task:', error);
        }
      }
      
      if (!otherCostsSaved) {
        // The task itself saved; keep the dialog open (now on the saved task) so the user sees
        // that Other Costs did not, and can save again (STATE-06).
        onTaskSaved(savedTask, { isNew: !formData.id, keepOpen: true });
        setSaveError("The task was saved, but its Other Costs weren't. Save again to retry.");
        return;
      }

      onTaskSaved(savedTask, { isNew: !formData.id });
      onClose();
    } catch (error) {
      console.error('Error saving task:', error);
      const message =
        error instanceof Error && error.message.trim()
          ? error.message.trim()
          : 'Failed to save task';
      setSaveError(message);
      if (isPlannedDateRelatedError(message)) {
        setActiveTab('planned_accomplishment');
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to save other costs as efforts
  /** Returns false when the Other Costs could not be saved (the task itself is already saved). */
  const saveOtherCostsAsEfforts = async (taskId: number): Promise<boolean> => {
    try {

      // If we failed to load existing other costs and the current list is empty,
      // avoid deleting existing efforts to prevent accidental data loss
      if (otherCosts.length === 0 && otherCostsLoadError) {
        // The Other Costs tab already says the list failed to load and will be left unchanged.
        console.warn('Skipping save of Other Costs: load previously failed and there are no local entries.');
        return true;
      }
      
      // First, delete any existing Other type efforts for this task
      await effortService.deleteEffortsByType(taskId, 'Other');

      // Convert other costs to effort format
      const effortInputs: CreateEffortInput[] = otherCosts.map((cost) => {
        let processedDate: string | undefined = undefined;
        
        if (cost.date && cost.date.trim()) {
          // Use UTC noon for API to avoid timezone shifting
          processedDate = yyyyMmDdToISONoonUTC(cost.date.trim());
          if (processedDate) {
          } else {
            const fallbackDate = new Date();
            processedDate = new Date(Date.UTC(
              fallbackDate.getUTCFullYear(),
              fallbackDate.getUTCMonth(),
              fallbackDate.getUTCDate(),
              12, 0, 0, 0
            )).toISOString();
          }
        } else {
          const now = new Date();
          processedDate = new Date(Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate(),
            12, 0, 0, 0
          )).toISOString();
        }
        
        const costNumber = Number(cost.cost) || 0;
        const markupNumber = Number(cost.markupPercent) || 0;
        
        return {
          taskId: taskId,
          type: 'Other',
          description: cost.description,
          quantity: costNumber, // Cost becomes quantity
          rate: 1 + (markupNumber / 100), // Markup percentage becomes rate (e.g., 20% markup = 1.2 rate)
          unitOfMeasure: 'CAD',
          date: processedDate,
          comments: cost.comments,
          employeeName: currentUser?.name + ' ' + currentUser?.lastName || 'System'
        };
      });


      // Save all other costs as efforts
      if (effortInputs.length > 0) {
        await effortService.createMultipleEfforts(effortInputs);
      }
      return true;
    } catch (error) {
      console.error('Error saving other costs as efforts:', error);
      // Don't break the task save; handleSubmit reports it inline.
      return false;
    }
  };

  // Function to group assets by their parent asset
  const groupAssetsByParent = (assets: Asset[], roadAssetsToUse: Asset[]) => {
    const groups: { [key: string]: Asset[] } = {};
    
    assets.forEach(asset => {
      let parentKey = 'Ungrouped';
      
      if (asset.parentAsset) {
        // Find the parent asset in roadAssets only, and ensure its type is 'road'
        const parentAsset = roadAssetsToUse.find(ra => 
          ra.assetNumber?.trim() === asset.parentAsset?.trim() && 
          ra.assetType?.toLowerCase() === 'road'
        );
        
        if (parentAsset && parentAsset.assetType?.toLowerCase() === 'road') {
          // Double-check that the parent asset is actually a road
          if (parentAsset.assetType?.toLowerCase() === 'road') {
            parentKey = asset.parentAsset.trim();
          } else {
            // If somehow a non-road asset was found, don't use it as parent
            console.warn('Found non-road parent asset:', parentAsset.assetNumber, parentAsset.assetType);
            parentKey = 'Ungrouped';
          }
        } else {
          // If the parent asset is not a road or not found, do not assign as parent
          parentKey = 'Ungrouped';
        }
      }
      
      if (!groups[parentKey]) {
        groups[parentKey] = [];
      }
      groups[parentKey].push(asset);
    });
    
    // Sort assets within each group alphabetically
    Object.keys(groups).forEach(key => {
      groups[key] = sortAssetsAlphabetically(groups[key]);
    });
    
    return groups;
  };

  // Place these inside the TaskModal component, before getParentAssetDescription
  const parentAssetCacheRef = useRef<{ [key: string]: Asset | null }>({});
  const [parentAssetLoading, setParentAssetLoading] = useState<{ [key: string]: boolean }>({});
  const parentAssetLookupInProgress = useRef<{ [key: string]: boolean }>({});

  const getParentAssetDescription = (parentAssetNumber: string): string => {
    if (!parentAssetNumber || parentAssetNumber === 'Ungrouped') {
      return 'Ungrouped Assets';
    }

    if (roadAssetsLoading) {
      return 'Loading parent asset...';
    }

    // Get the current service area from the selected foreman area
    const selectedForemanArea = foremanAreas.find(area => area.id === Number(formData.foremanArea));
    const serviceAreaId = selectedForemanArea?.serviceAreaId;

    // Try to find in local roadAssets first
    const parentAsset = roadAssets.find(asset =>
      asset.assetNumber?.trim() === parentAssetNumber.trim()
    );

    if (parentAsset) {
      const name = parentAsset.description?.trim() || parentAsset.name?.trim() || 'Road';
      return `${name} (#${parentAssetNumber})`;
    }

    // If not found, check cache with service area consideration
    const cacheKey = `${parentAssetNumber}_${serviceAreaId}`;
    if (parentAssetCacheRef.current[cacheKey]) {
      const cached = parentAssetCacheRef.current[cacheKey];
      if (cached) {
        const name = cached.description?.trim() || cached.name?.trim() || 'Road';
        return `${name} (#${parentAssetNumber})`;
      }
      // If cached as null, fall through to default
    }

    // If loading, show loading message
    if (parentAssetLoading[cacheKey]) {
      return 'Loading parent asset...';
    }

    // If not found, trigger async search with service area and show default for now
    if (!parentAssetLookupInProgress.current[cacheKey] && serviceAreaId) {
      parentAssetLookupInProgress.current[cacheKey] = true;
      (async () => {
        setParentAssetLoading(prev => ({ ...prev, [cacheKey]: true }));
        try {
          const found = await assetService.searchAssets({ 
            assetNumber: parentAssetNumber,
            serviceAreaId: serviceAreaId
          });
          if (found && found.length > 0) {
            parentAssetCacheRef.current[cacheKey] = found[0];
          } else {
            parentAssetCacheRef.current[cacheKey] = null;
          }
        } catch (e) {
          console.error('Error finding parent asset:', e);
          parentAssetCacheRef.current[cacheKey] = null;
        } finally {
          setParentAssetLoading(prev => ({ ...prev, [cacheKey]: false }));
          parentAssetLookupInProgress.current[cacheKey] = false;
        }
      })();
    }

    return `Road (#${parentAssetNumber})`;
  };

  const loadAssets = async (contractAreaId: number, assetType: AssetTypeEnum | undefined) => {
    if (!contractAreaId || !assetType) return;
    
    // Get the current service area from the selected foreman area
    const selectedForemanArea = foremanAreas.find(area => area.id === Number(formData.foremanArea));
    const serviceAreaId = selectedForemanArea?.serviceAreaId;
    
    try {
      setAssetLoading(true);
      const response = await assetService.searchAssets({
        contractAreaId,
        assetType: assetType.toLowerCase(),
        ...(serviceAreaId && { serviceAreaId })
      });
      
      // Ensure response is an array
      if (!Array.isArray(response)) {
        console.warn('Asset service returned non-array response:', response);
        setAssets([]);
        return;
      }
      
      // For non-road assets, check if they have parentAsset property 
      if (assetType.toLowerCase() !== 'road') {
        // Load road assets at service area level for better grouping of structures
        // This ensures we get all roads that could be parents for structures in this service area
        const roadAssetsResponse = await assetService.searchAssets({
          assetType: 'road',
          ...(serviceAreaId ? { serviceAreaId } : { contractAreaId })
        });
        
        
        // Filter to ensure only road assets
        const validRoadAssets = Array.isArray(roadAssetsResponse) ? roadAssetsResponse.filter(asset => 
          asset.assetType?.toLowerCase() === 'road'
        ) : [];
        
        
        // Then process the non-road assets to ensure they have parent references
        const processedAssets = response.map(asset => {
          
          // Check if asset already has a parentAsset property
          if (!asset.parentAsset) {
            // If no parentAsset is set, try to set it based on naming conventions
            // Often assets are named with a parent road number prefix
            const assetNumber = asset.assetNumber?.trim();
            if (assetNumber && assetNumber.includes('-')) {
              // Extract potential parent number (before the dash)
              const parentNumber = assetNumber.split('-')[0].trim();
              
              // Check if this parent exists in our road assets AND is actually a road type
              const parentExists = validRoadAssets.some(road => 
                road.assetNumber?.trim() === parentNumber && 
                road.assetType?.toLowerCase() === 'road'
              );
              
              if (parentExists) {
                // Clone the asset and add the parent reference
                return {
                  ...asset,
                  parentAsset: parentNumber
                };
              } else {
              }
            } else {
            }
          } else {
            // If asset already has a parentAsset, validate it's a valid road
            const existingParent = validRoadAssets.find(road => 
              road.assetNumber?.trim() === asset.parentAsset?.trim()
            );
            
            if (!existingParent || existingParent.assetType?.toLowerCase() !== 'road') {
              console.warn(`Asset ${asset.assetNumber} has invalid parent ${asset.parentAsset}, clearing parent reference`);
              return {
                ...asset,
                parentAsset: undefined
              };
            } else {
            }
          }
          return asset;
        });
        
        
        // Validate and clean up parent asset references
        const validatedAssets = validateParentAssetReferences(processedAssets, validRoadAssets);
        
        // Use the validated assets
        setAssets(validatedAssets);
        
        // Group the assets by parent for the MultiTierDropdown
        const grouped = groupAssetsByParent(validatedAssets, validRoadAssets);
        setGroupedAssets(grouped);
      } else {
        // For roads, sort alphabetically before setting
        setAssets(sortAssetsAlphabetically(response));
      }
    } catch (error) {
      console.error('Error loading assets:', error);
      setAssets([]);
    } finally {
      setAssetLoading(false);
    }
  };

  const loadRoadAssets = async (contractAreaId: number) => {
    // Get the current service area from the selected foreman area
    const selectedForemanArea = foremanAreas.find(area => area.id === Number(formData.foremanArea));
    const serviceAreaId = selectedForemanArea?.serviceAreaId;
    
    try {
      setRoadAssetsLoading(true);
      const response = await assetService.searchAssets({
        assetType: 'road', // Ensure lowercase 'road' is used consistently
        ...(serviceAreaId ? { serviceAreaId } : { contractAreaId })
      });
      
      // Ensure response is an array
      if (!Array.isArray(response)) {
        console.warn('Road asset service returned non-array response:', response);
        setRoadAssets([]);
        return;
      }
      
      // Double-check that all returned assets are actually road type
      const validRoadAssets = response.filter(asset => {
        const isRoad = asset.assetType?.toLowerCase() === 'road';
        if (!isRoad) {
          console.warn('Non-road asset returned when searching for roads:', asset.assetNumber, asset.assetType);
        }
        return isRoad;
      });
      
      setRoadAssets(validRoadAssets);
    } catch (error) {
      console.error('Error loading road assets:', error);
      setRoadAssets([]);
    } finally {
      setRoadAssetsLoading(false);
    }
  };

  // Update the handleLocationSelect function
  const handleLocationSelect = (lat: number, lng: number, type: 'start' | 'end') => {
    setSelectedRouteInfo(null);
    if (type === 'start') {
      setFormData(prev => ({
        ...prev,
        startLatitude: lat.toString(),
        startLongitude: lng.toString()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        endLatitude: lat.toString(),
        endLongitude: lng.toString()
      }));
    }
  };

  const loadAttachments = async () => {
    if (!task?.id) return;
    try {
      const fetchedAttachments = await attachmentService.getTaskAttachments(task.id.toString());
      
      // Ensure fetchedAttachments is an array
      if (!Array.isArray(fetchedAttachments)) {
        console.warn("AttachmentService returned non-array response:", fetchedAttachments);
        setAttachments([]);
        return;
      }
      
      setAttachments(fetchedAttachments);
    } catch (error) {
      console.error('Error loading attachments:', error);
      setAttachments([]);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !task?.id) return;
    setPendingFile(file);
  };

  const handleNoteSave = async (note: string) => {
    if (!pendingFile || !task?.id) return;

    try {
      setUploading(true);
      setAttachmentError(null);
      await attachmentService.uploadAttachment(task.id.toString(), pendingFile, note.trim() || undefined);
      await loadAttachments();
      
      // Refresh the task to get the latest history (attachment uploads create history entries)
      try {
        const refreshedTask = await taskService.getTask(task.id);
        onTaskSaved(refreshedTask, { keepOpen: true });
      } catch (error) {
        console.warn('Failed to refresh task after attachment upload:', error);
        // Continue even if refresh fails
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setAttachmentError(describeActionError(error, "Couldn't upload the file. Try again."));
    } finally {
      setUploading(false);
      setPendingFile(null);
      // Clear the file input
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    setDeletingAttachmentId(attachmentId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deletingAttachmentId || !task?.id) return;

    try {
      setAttachmentError(null);
      await attachmentService.deleteAttachment(deletingAttachmentId.toString());
      // Update the attachments list locally instead of fetching
      setAttachments(prev => prev.filter(a => a.id !== deletingAttachmentId));
      
      // Refresh the task to get the latest history (attachment deletions may create history entries)
      try {
        const refreshedTask = await taskService.getTask(task.id);
        onTaskSaved(refreshedTask, { keepOpen: true });
      } catch (error) {
        console.warn('Failed to refresh task after attachment deletion:', error);
        // Continue even if refresh fails
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setAttachmentError(describeActionError(error, "Couldn't delete the attachment. Try again."));
    } finally {
      setShowDeleteConfirm(false);
      setDeletingAttachmentId(null);
    }
  };

  // Add this function before handleSubmit
  const getFormData = (): TaskCreateInput => {
    // Process the total value - strip currency symbols if present
    let totalValue = formData.total;
    if (typeof totalValue === 'string' && totalValue.includes('$')) {
      totalValue = stripCurrency(totalValue);
    }
    
    // Find the selected activity to get its number
    const selectedActivity = activities.find(a => a.id === formData.activityId);
    
    // Set identifiedDate to current date if status is Active and no date is set
    const identifiedDate = formData.status === TaskStage.ACTIVE && !formData.identifiedDate
      ? new Date()
      : formData.identifiedDate ? new Date(formData.identifiedDate) : new Date();
    
    // Handle project ID - ensure we use the project from props if form data is empty
    let projectId: number | undefined = undefined;
    
    
    if (formData.project && formData.project !== '') {
      projectId = Number(formData.project);
    } else if (formData.project === '') {
      // User explicitly selected "Select Project" (empty value) - remove project
      projectId = undefined;
    } else if (initialProject?.id) {
      // Only use initialProject if formData.project is not explicitly set to empty
      projectId = initialProject.id;
    } else {
      console.warn('No project ID found in form data or initialProject prop');
    }
    
    // Log the final project ID being sent
    
    // Create the base form data
    const baseFormData = {
      activityId: Number(formData.activityId),
      activityNumber: selectedActivity?.activityNumber || '', // Ensure activity number is a string
      priority: formData.priority || 'LOW',
      description: formData.description || null,
      instructions: formData.instructions || null,
      name: formData.name || null,
      identifiedDate: identifiedDate,
      completionDate: formData.completionDate ? new Date(formData.completionDate) : null,
      contractYear: formData.contractYear || '',
      projectId: projectId,
      estimatedEffortHours: formData.estimatedEffortHours && formData.estimatedEffortHours !== '' ? Number(formData.estimatedEffortHours) : null,
      plannedCompletionDate: formData.plannedCompletionDate && formData.plannedCompletionDate !== '' ? new Date(formData.plannedCompletionDate) : null,
      foremanArea: formData.foremanArea ? Number(formData.foremanArea) : null,
      contractArea: formData.contractArea ? Number(formData.contractArea) : null,
      comments: mergeScheduleDependenciesIntoComments(
        formData.comments || null,
        formData.dependencyTriggers && formData.dependencyTriggers.length > 0
          ? {
              triggers: formData.dependencyTriggers,
              otherComment: formData.dependencyOtherComment || null,
              resourceDependentType: formData.dependencyResourceType || null,
              completionTaskId: formData.dependencyCompletionTaskId
                ? Number(formData.dependencyCompletionTaskId)
                : null,
            }
          : null
      ),
      taskType: formData.taskType || 'REGULAR',
      status: formData.status || 'ACTIVE',
      quantifiedTasks: [{
        identifiedQuantity: Number(formData.identifiedQuantity) || 0,
        unitOfMeasure: formData.unitOfMeasure || '',
      }],
      taskLocations: [
        {
          type: TaskLocationType.START,
          latitude: Number(formData.startLatitude),
          longitude: Number(formData.startLongitude),
          assetId: formData.assetId || null,
        },
        ...(formData.endLatitude && formData.endLongitude ? [{
          type: TaskLocationType.END,
          latitude: Number(formData.endLatitude),
          longitude: Number(formData.endLongitude),
          assetId: formData.assetId || null,
        }] : [])
      ],
      taskCostings: [{
        baseRate: Number(formData.baseRate) || 0,
        rateMultiplier: formData.taskType === 'Lump Sum' ? 1 : Number(formData.rateMultiplier) || 1,
        rateAdjustment: formData.taskType === 'Lump Sum' ? 0 : Number(formData.rateAdjustment) || 0,
        totalValue: Number(totalValue) || 0,
        actualTotalValue: shouldForceActualTotalValueToZero(formData.status)
          ? 0
          : actualTotalValueInput !== undefined && actualTotalValueInput !== null
            ? Number(actualTotalValueInput)
            : undefined,
        taskType: formData.taskType,
        costType: formData.costType,
        rateAdjustmentComments: formData.rateAdjustmentComments || null,
        labourMarkup: formData.taskType === 'Lump Sum' && formData.costType === 'Cost Plus' ? Number(formData.labourMarkup) : 0,
        materialMarkup: formData.taskType === 'Lump Sum' && formData.costType === 'Cost Plus' ? Number(formData.materialMarkup) : 0,
        subcontractorMarkup: formData.taskType === 'Lump Sum' && formData.costType === 'Cost Plus' ? Number(formData.subcontractorMarkup) : 0,
        vehicleMarkup: formData.taskType === 'Lump Sum' && formData.costType === 'Cost Plus' ? Number(formData.vehicleMarkup) : 0,
        hiredEquipmentMarkup: formData.taskType === 'Lump Sum' && formData.costType === 'Cost Plus' ? Number(formData.hiredEquipmentMarkup) : 0
      }],
      assignedEmployeeIds: formData.assignedEmployeeIds || [],
      assignedVehicleUnitNumbers: pickRepresentativeUnitsForTypes(
        formData.assignedVehicleTypes || [],
        availableVehicles,
        formData.foremanArea
          ? foremanAreas.find((a) => a.id === Number(formData.foremanArea))?.serviceAreaId ?? null
          : null
      ),
    };
    
    // If we're updating an existing task, include the ID
    if (task?.id) {
      return {
        ...baseFormData,
        id: task.id,
      };
    }
    
    return baseFormData;
  };

  // Update the input fields to use the style when closed
  const renderBasicInfo = () => (
    <div className="space-y-4">
      {/* Task Number and Status in two columns */}
      <Row className="tw-mb-3">
        <Col md={6}>
          <Form.Label>Task ID</Form.Label>
          <Form.Control
            type="text"
            value={formData.taskNumber || ''}
            readOnly
            style={readOnlyStyle}
            placeholder="Task number will be assigned when saved"
          />
        </Col>
        <Col md={6}>
          <RequiredLabel text="Status" />
          {task?.status === TaskStage.ACTIVE_MARKED_AS_COMPLETE || task?.status === TaskStage.CLOSED || task?.status === TaskStage.INTERNAL_REVIEW || task?.status === TaskStage.FINALIZED || !hasPermission('quantified:status_change') ? (
            <Form.Control
              type="text"
              value={TASK_STATUSES.find(s => s.value === formData.status)?.label || ''}
              readOnly
              style={readOnlyStyle}
            />
          ) : (
            <Form.Select
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              disabled={isTaskLocked}
              style={isTaskLocked ? lockedFieldStyle : undefined}
            >
              {Array.isArray(AVAILABLE_STATUSES) && AVAILABLE_STATUSES.map(status => (
                <option key={status} value={status}>
                  {TASK_STATUSES.find(s => s.value === status)?.label || ''}
                </option>
              ))}
            </Form.Select>
          )}
        </Col>
      </Row>

      {/* Add Task Name field */}
      <Row className="tw-mb-3">
        <Col md={6}>
          <RequiredLabel text="Task Name" />
          <Form.Control
            type="text"
            name="name"
            id={taskFieldId('name')}
            isInvalid={!!fieldErrors.name}
            aria-describedby={fieldErrors.name ? `${taskFieldId('name')}-error` : undefined}
            value={formData.name || ''}
            onChange={handleChange}
            required
            readOnly={isTaskLocked || isReadOnlyForFinanceReporter}
            style={(isTaskLocked || isReadOnlyForFinanceReporter) ? lockedFieldStyle : undefined}
            placeholder="Task name will be auto-generated when activity and asset are selected"
          />
          {renderFieldError('name')}
        </Col>
      </Row>

      {/* Contract Year and Priority in two columns */}
      <Row className="tw-mb-3">
        <Col md={6}>
          <RequiredLabel text="Contract Year" />
          <Form.Select
            name="contractYear"
            id={taskFieldId('contractYear')}
            isInvalid={!!fieldErrors.contractYear}
            aria-describedby={fieldErrors.contractYear ? `${taskFieldId('contractYear')}-error` : undefined}
            value={formData.contractYear || ''}
            onChange={handleChange}
            required
            disabled={isTaskLocked || !formData.foremanArea}
            style={isTaskLocked ? lockedFieldStyle : undefined}
          >
            <option value="">
              {loadingQuantifiedPlans
                ? 'Loading contract years…'
                : !formData.foremanArea
                  ? 'Select Foreman Area first'
                  : 'Select Contract Year'}
            </option>
            {!loadingQuantifiedPlans &&
              openContractYears.map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            {!loadingQuantifiedPlans && legacyContractYear && (
              <option key={`legacy-${legacyContractYear.year}`} value={String(legacyContractYear.year)}>
                {legacyContractYear.year} {legacyContractYear.labelSuffix}
              </option>
            )}
          </Form.Select>
          {renderFieldError('contractYear')}
          {!loadingQuantifiedPlans &&
            formData.foremanArea &&
            openContractYears.length === 0 &&
            !legacyContractYear && (
              <Form.Text className="tw-text-content-muted">
                No open quantified plans for this service area. Open a plan under Areas → Quantified Plans.
              </Form.Text>
            )}
        </Col>
        <Col md={6}>
          <RequiredLabel text="Priority" />
          <Form.Select
            name="priority"
            value={formData.priority || ''}
            onChange={handleChange}
            required
            disabled={isTaskLocked}
            style={isTaskLocked ? lockedFieldStyle : undefined}
          >
            <option value="">Select Priority</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Foreman Area and Project in two columns */}
      <Row className="tw-mb-3">
        <Col md={6}>
          <RequiredLabel text="Foreman Area" />
          <Form.Select
            name="foremanArea"
            id={taskFieldId('foremanArea')}
            isInvalid={!!fieldErrors.foremanArea}
            aria-describedby={fieldErrors.foremanArea ? `${taskFieldId('foremanArea')}-error` : undefined}
            value={formData.foremanArea || ''}
            onChange={handleForemanAreaChange}
            required
            disabled={isTaskLocked || isReadOnlyForFinanceReporter}
            style={(isTaskLocked || isReadOnlyForFinanceReporter) ? lockedFieldStyle : undefined}
          >
            <option value="">Select Foreman Area</option>
            {Array.isArray(foremanAreas) && foremanAreas.map(area => (
              <option key={area.id} value={area.id}>
                {area.name.toLowerCase().includes('bridge')
                  ? area.name
                  : area.code
                    ? `${area.name} (${area.code})`
                    : area.name}
              </option>
            ))}
          </Form.Select>
          {renderFieldError('foremanArea')}
        </Col>
        <Col md={6}>
          <Form.Label>
            Project {initialProject ? '(Pre-selected from Project Details)' : '(Optional)'}
          </Form.Label>
          <Form.Select
            name="project"
            id={taskFieldId('project')}
            isInvalid={!!fieldErrors.project}
            aria-describedby={fieldErrors.project ? `${taskFieldId('project')}-error` : undefined}
            value={formData.project || ''}
            onChange={handleChange}
            disabled={
              isTaskLocked ||
              (Boolean(initialProject) &&
                !task &&
                initialProject!.status?.toLowerCase() !== 'completed') ||
              isReadOnlyForFinanceReporter
            }
            style={
              isTaskLocked ||
              (Boolean(initialProject) &&
                !task &&
                initialProject!.status?.toLowerCase() !== 'completed') ||
              isReadOnlyForFinanceReporter
                ? lockedFieldStyle
                : undefined
            }
          >
            <option value="">Select Project</option>
            {Array.isArray(filteredProjects) && filteredProjects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
                {initialProject && project.id === initialProject.id ? ' (Current Project)' : ''}
              </option>
            ))}
          </Form.Select>
          {renderFieldError('project')}
          {projectsLoadError && (
            <LoadErrorState compact what="projects" onRetry={() => setProjectsReloadKey((k) => k + 1)} />
          )}
          {initialProject && !task && initialProject.status?.toLowerCase() !== 'completed' && (
            <small className="tw-text-success">
              <i className="bi bi-check-circle tw-mr-1"></i>
              Project automatically selected from project details page
            </small>
          )}
          {!initialProject && !formData.foremanArea && (
            <small className="tw-text-content-muted">
              Select a foreman area to filter projects
            </small>
          )}
          {!initialProject && formData.foremanArea && filteredProjects.length === 0 && (
            <small className="tw-text-content-muted">
              No active projects available for the selected service area
            </small>
          )}
        </Col>
      </Row>

      <Form.Group className="tw-mb-3">
        <Form.Label>Internal Notes / Description (Optional)</Form.Label>
        <Form.Control
          as="textarea"
          value={formData.description || ''}
          onChange={handleChange}
          name="description"
          placeholder="Enter internal notes or description..."
          rows={4}
          readOnly={isTaskLocked}
          style={isTaskLocked ? lockedFieldStyle : undefined}
        />
      </Form.Group>

      <Form.Group className="tw-mb-3">
        <Form.Label>Instructions for Operator (Optional)</Form.Label>
        <Form.Control
          as="textarea"
          value={formData.instructions || ''}
          onChange={handleChange}
          name="instructions"
          placeholder="Enter instructions for the operator..."
          rows={4}
          readOnly={isTaskLocked}
          style={isTaskLocked ? lockedFieldStyle : undefined}
        />
      </Form.Group>

      <Form.Group className="tw-mb-3">
        <Form.Label>External Comments (will be exported in the D3) (Optional)</Form.Label>
        <Form.Control
          as="textarea"
          name="comments"
          value={formData.comments || ''}
          onChange={handleChange}
          readOnly={isTaskLocked || !hasPermission('quantified:comments_create')}
          style={(isTaskLocked || !hasPermission('quantified:comments_create')) ? lockedFieldStyle : undefined}
        />
      </Form.Group>

      {/* Work Planning Section - Only for Specific tasks (not Standing) */}
      {task?.mode?.toLowerCase() !== 'standing' && (() => {
        const isWorkPlanningRequired = formData.status === TaskStage.ACTIVE || formData.status === TaskStage.PLANNED;
        const isProposed = formData.status === TaskStage.PROPOSED;
        const canEditWorkPlanning = hasPermission('quantified:work_planning_edit') && (isWorkPlanningRequired || isProposed);
        const isWorkPlanningReadOnly = isTaskLocked || !canEditWorkPlanning;
        
        // Calculate Estimated Duration (Calendar Days)
        const calculateEstimatedDuration = (): number | null => {
          if (!formData.plannedCompletionDate || !formData.identifiedDate) {
            return null;
          }
          try {
            const startDate = new Date(formData.identifiedDate);
            const endDate = new Date(formData.plannedCompletionDate);
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
              return null;
            }
            const diffTime = endDate.getTime() - startDate.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays >= 0 ? diffDays : null;
          } catch {
            return null;
          }
        };
        
        const estimatedDurationDays = calculateEstimatedDuration();

        const plannedStartYmd = (formData.identifiedDate || '').trim();
        const plannedEndYmd = (formData.plannedCompletionDate || '').trim();

        return (
          <div className="tw-mb-3">
            <Card>
              <Card.Header>
                <h6 className="tw-m-0">Work Planning</h6>
              </Card.Header>
              <Card.Body>
                {contractYearDateMismatch && (
                  <Alert variant="warning" className="tw-mb-3">
                    <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                    {formatContractYearDateMismatchMessage(contractYearDateMismatch)}
                    {!contractYearDateMismatch.isOpenPlan && (
                      <span className="tw-block tw-mt-1 tw-text-sm">
                        This contract year uses a closed or legacy plan; you may still save, but please verify the dates.
                      </span>
                    )}
                  </Alert>
                )}
                <Row className="tw-mb-3">
                  <Col md={6}>
                    {isWorkPlanningRequired ? (
                      <RequiredLabel text="Planned Start Date" />
                    ) : (
                      <Form.Label>Planned Start Date</Form.Label>
                    )}
                    <Form.Control
                      type="date"
                      id={taskFieldId('identifiedDate')}
                      isInvalid={Boolean(fieldErrors.identifiedDate || (saveError && isPlannedDateRelatedError(saveError)))}
                      aria-describedby={fieldErrors.identifiedDate ? `${taskFieldId('identifiedDate')}-error` : undefined}
                      name="identifiedDate"
                      value={formData.identifiedDate || ''}
                      onChange={handleChange}
                      required={isWorkPlanningRequired}
                      readOnly={isWorkPlanningReadOnly}
                      max={!isWorkPlanningReadOnly && plannedEndYmd ? plannedEndYmd : undefined}
                      style={isWorkPlanningReadOnly ? readOnlyStyle : undefined}
                    />
                    {renderFieldError('identifiedDate')}
                    {saveError && isPlannedDateRelatedError(saveError) && (
                      <Form.Control.Feedback type="invalid" className="tw-block">{saveError}</Form.Control.Feedback>
                    )}
                  </Col>
                  <Col md={6}>
                    {isWorkPlanningRequired ? (
                      <RequiredLabel text="Planned Completion Date" />
                    ) : (
                      <Form.Label>Planned Completion Date</Form.Label>
                    )}
                    <Form.Control
                      type="date"
                      id={taskFieldId('plannedCompletionDate')}
                      isInvalid={Boolean(fieldErrors.plannedCompletionDate || (saveError && isPlannedDateRelatedError(saveError)))}
                      aria-describedby={fieldErrors.plannedCompletionDate ? `${taskFieldId('plannedCompletionDate')}-error` : undefined}
                      name="plannedCompletionDate"
                      value={formData.plannedCompletionDate || ''}
                      onChange={handleChange}
                      required={isWorkPlanningRequired}
                      readOnly={isWorkPlanningReadOnly}
                      min={!isWorkPlanningReadOnly && plannedStartYmd ? plannedStartYmd : undefined}
                      style={isWorkPlanningReadOnly ? readOnlyStyle : undefined}
                    />
                    {renderFieldError('plannedCompletionDate')}
                  </Col>
                </Row>
                <Row className="tw-mb-3">
                  <Col md={6}>
                    <Form.Label>Estimated Effort (Hours)</Form.Label>
                    <Form.Control
                      type="number"
                      name="estimatedEffortHours"
                      value={formData.estimatedEffortHours || ''}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      readOnly={isWorkPlanningReadOnly}
                      style={isWorkPlanningReadOnly ? readOnlyStyle : undefined}
                      placeholder="Enter estimated hours"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Estimated Duration (Calendar Days)</Form.Label>
                    <Form.Control
                      type="text"
                      value={estimatedDurationDays !== null ? estimatedDurationDays.toString() : ''}
                      readOnly
                      style={readOnlyStyle}
                      placeholder="Calculated automatically"
                    />
                    <small className="tw-text-content-muted">
                      Calculated from Planned Completion Date minus Planned Start Date
                    </small>
                  </Col>
                </Row>
                <Row className="tw-mb-3">
                  <Col md={6}>
                    <MultiSelectDropdown<User>
                      options={employees}
                      value={formData.assignedEmployeeIds?.map(id => Number(id)) || []}
                      onChange={(selectedIds) => {
                        setFormData(prev => ({
                          ...prev,
                          assignedEmployeeIds: selectedIds.map(id => Number(id))
                        }));
                      }}
                      formatOption={(employee) => `${employee.name} ${employee.lastName}${employee.employeeNumber ? ` (${employee.employeeNumber})` : ''}`}
                      formatDisplay={(employee) => `${employee.name} ${employee.lastName}${employee.employeeNumber ? ` (${employee.employeeNumber})` : ''}`}
                      getValue={(employee) => employee.id}
                      placeholder={loadingEmployees ? "Loading employees..." : employees.length === 0 ? "No active employees found" : "Select employees"}
                      disabled={isWorkPlanningReadOnly}
                      readOnly={isWorkPlanningReadOnly}
                      readOnlyStyle={readOnlyStyle}
                      label="Assigned Employees"
                      noOptionsMessage={loadingEmployees ? "Loading employees..." : "No active employees found"}
                      showSelectedCount={true}
                    />

                    {/* Debug info */}
                    {process.env.NODE_ENV === 'development' && (
                      <div style={{ fontSize: '10px', color: 'gray', marginTop: '4px' }}>
                        Debug: formData.assignedEmployeeIds = {JSON.stringify(formData.assignedEmployeeIds)},
                        employees count = {employees.length}
                      </div>
                    )}
                  </Col>

                  <Col md={6}>
                    <div id={taskFieldId('assignedVehicleTypes')}>
                    <MultiSelectDropdown<{ type: string; capacity: number; availableCapacity: number }>
                      options={vehicleEquipmentTypeOptions}
                      value={formData.assignedVehicleTypes || []}
                      onChange={(selectedTypes) => {
                        setFormData((prev) => ({
                          ...prev,
                          assignedVehicleTypes: selectedTypes.map((v) => String(v)),
                        }));
                      }}
                      formatOption={(o) => `${o.type} (${o.availableCapacity} available)`}
                      formatDisplay={(o) => o.type}
                      getValue={(o) => o.type}
                      placeholder={
                        loadingVehicles
                          ? 'Loading equipment types...'
                          : vehicleEquipmentTypeOptions.length === 0
                            ? 'No equipment types for this service area'
                            : 'Select equipment types'
                      }
                      disabled={isWorkPlanningReadOnly}
                      readOnly={isWorkPlanningReadOnly}
                      readOnlyStyle={readOnlyStyle}
                      label="Assigned Equipments"
                      noOptionsMessage={
                        loadingVehicles
                          ? 'Loading equipment types...'
                          : 'No equipment types for this service area'
                      }
                      showSelectedCount={true}
                    />
                    </div>
                    {/* The fetch-failed / availability messages below already show under the field;
                        only the "still loading" save error needs its own line. */}
                    {fieldErrors.assignedVehicleTypes &&
                      !(equipmentAvailabilityFetchFailed && !isWorkPlanningReadOnly) &&
                      !(equipmentAvailabilityMessage && !isWorkPlanningReadOnly) &&
                      renderFieldError('assignedVehicleTypes')}
                    {equipmentAvailabilityFetchFailed && !isWorkPlanningReadOnly && (
                      <div className="tw-text-danger tw-text-sm tw-mt-1" role="alert">
                        Could not verify equipment availability. Please try again.
                      </div>
                    )}
                    {equipmentAvailabilityMessage && !isWorkPlanningReadOnly && (
                      <div className="tw-text-danger tw-text-sm tw-mt-1" role="alert">
                        {equipmentAvailabilityMessage}
                      </div>
                    )}
                    {loadingScheduleAvailability && formData.assignedVehicleTypes && formData.assignedVehicleTypes.length > 0 && (
                      <div className="tw-text-content-muted tw-text-sm tw-mt-1">Checking equipment availability…</div>
                    )}
                  </Col>
                </Row>
                <Row className="tw-mb-3">
                  <Col md={6}>
                    <Form.Label>Task Dependencies</Form.Label>
                    <div className="tw-border tw-border-line tw-rounded tw-p-2 tw-bg-surface-alt">
                      {DEPENDENCY_TRIGGER_OPTIONS.map((opt) => {
                        const checked = (formData.dependencyTriggers || []).includes(opt);
                        return (
                          <Form.Check
                            key={opt}
                            type="checkbox"
                            id={`dependency-trigger-${opt}`}
                            label={opt}
                            checked={checked}
                            disabled={isWorkPlanningReadOnly}
                            onChange={(e) => {
                              const next = new Set(formData.dependencyTriggers || []);
                              if (e.target.checked) next.add(opt);
                              else next.delete(opt);
                              setFormData((prev) => ({
                                ...prev,
                                dependencyTriggers: Array.from(next),
                                dependencyOtherComment: opt === 'Other' && !e.target.checked ? '' : prev.dependencyOtherComment,
                              }));
                            }}
                            className="tw-mb-1"
                          />
                        );
                      })}
                    </div>
                    {(formData.dependencyTriggers || []).includes('Other') && (
                      <>
                        <Form.Control
                          className="tw-mt-2"
                          type="text"
                          id={taskFieldId('dependencyOtherComment')}
                          isInvalid={!!fieldErrors.dependencyOtherComment}
                          aria-describedby={fieldErrors.dependencyOtherComment ? `${taskFieldId('dependencyOtherComment')}-error` : undefined}
                          placeholder='Required comment for "Other" dependency'
                          value={formData.dependencyOtherComment || ''}
                          readOnly={isWorkPlanningReadOnly}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, dependencyOtherComment: e.target.value }))
                          }
                        />
                        {renderFieldError('dependencyOtherComment')}
                      </>
                    )}
                  </Col>
                  <Col md={6}>
                    <Form.Label>Resource Dependant</Form.Label>
                    <Form.Select
                      value={formData.dependencyResourceType || ''}
                      disabled={isWorkPlanningReadOnly}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, dependencyResourceType: e.target.value }))
                      }
                    >
                      <option value="">None</option>
                      {dependencyResourceOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Label className="tw-mt-2">Completion of Another Task</Form.Label>
                    <Form.Select
                      value={formData.dependencyCompletionTaskId || ''}
                      disabled={isWorkPlanningReadOnly}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, dependencyCompletionTaskId: e.target.value }))
                      }
                    >
                      <option value="">None</option>
                      {dependencyTaskOptions.map((opt) => (
                        <option key={opt.id} value={String(opt.id)}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                    {!formData.foremanArea && (
                      <Form.Text className="tw-text-content-muted tw-block tw-mt-1">
                        Select a foreman area in Basic Info to load tasks for this dropdown (Proposed, Planned, Active, Active
                        marked complete).
                      </Form.Text>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        );
      })()}
    </div>
  );

  const renderLocation = () => {
    // Get the selected activity to check if it's a point type
    const selectedActivity = activities.find(a => a.id === formData.activityId);
    const isPointActivity = selectedActivity?.point === true && selectedActivity?.linear === false;
    
    // Also treat Sign assets as point activities
    const isSignAsset = formData.assetType?.toLowerCase() === 'sign';
    const treatAsPointActivity = isPointActivity || isSignAsset;

    return (
      <>
        {/* Contract Area and Asset Type in the same row */}
        <Row className="tw-mb-3">
          <Col md={6}>
            <RequiredLabel text="Contract Area" />
            <Form.Select
              name="contractArea"
              value={formData.contractArea || ''}
              onChange={(e) => {
                const newContractArea = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  contractArea: newContractArea,
                  assetType: undefined,
                  assetId: null
                }));

                // Load road assets immediately when contract area changes
                if (newContractArea) {
                  loadRoadAssets(Number(newContractArea));
                }
              }}
              disabled={isTaskLocked || contractAreas.length === 0}
              style={isTaskLocked ? lockedFieldStyle : undefined}
              required
            >
              <option value="">Select Contract Area</option>
              {Array.isArray(contractAreas) && contractAreas.map(area => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </Form.Select>
            {contractAreas.length === 0 && (
              <small className="tw-text-content-muted">
                {!formData.foremanArea
                  ? 'Select a Foreman Area on Basic Info first'
                  : 'No contract areas found for this Foreman Area'}
              </small>
            )}
          </Col>
          <Col md={6}>
            <RequiredLabel text="Asset Type" />
            <Form.Select
              name="assetType"
              value={formData.assetType || ''}
              onChange={handleChange}
              disabled={isTaskLocked}
              style={isTaskLocked ? lockedFieldStyle : undefined}
              required
            >
              <option value="">Select Asset Type</option>
              {(Object.values(AssetType) as AssetType[]).map(type => (
                <option key={type} value={type}>
                  {ASSET_TYPE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {/* Asset in full width */}
        <Row className="tw-mb-3">
          <Col xs={12}>
            <RequiredLabel text="Asset" />
            {assetLoading ? (
              <div className="tw-block tw-w-full tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-1.5 tw-text-content-muted">
                <i className="bi bi-hourglass-split tw-mr-2"></i>
                Loading assets...
              </div>
            ) : contractAreas.length === 0 ? (
              <div className="tw-block tw-w-full tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-1.5 tw-text-content-muted">
                {!formData.foremanArea
                  ? 'Select a Foreman Area on Basic Info first'
                  : 'No contract areas found for this Foreman Area'}
              </div>
            ) : !formData.assetType ? (
              <div className="tw-block tw-w-full tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-1.5 tw-text-content-muted">Select an asset type first</div>
            ) : (
              <MultiTierDropdown
                items={assets}
                value={formData.assetId || undefined}
                onChange={(value) => {
                  const selectedAsset = assets.find(a => a.id === value);
                  if (selectedAsset) {
                    const selectedActivity = activities.find(a => a.id === formData.activityId);
                    const generatedName = generateTaskName(selectedActivity, selectedAsset);
                    setFormData(prev => ({
                      ...prev,
                      assetId: value,
                      name: (!isTaskNameManuallyEditedRef.current || !String(prev.name || '').trim())
                        ? generatedName
                        : prev.name,
                    }));
                  }
                }}
                disabled={!formData.assetType || !formData.contractArea || isTaskLocked}
                loading={assetLoading}
                placeholder="Select an asset"
                noItemsMessage={assets.length === 0 ? "No assets found for the selected criteria" : "No matching assets found"}
                getParentDescription={getParentAssetDescription}
                assetType={formData.assetType}
              />
            )}
          </Col>
        </Row>

        {/* Road Asset Information Display */}
        {formData.assetId && formData.assetType?.toLowerCase() === 'road' && (() => {
          const selectedAsset = assets.find(a => a.id === formData.assetId);
          if (selectedAsset) {
            return (
              <Row className="tw-mb-3">
                <Col xs={12}>
                  <Card>
                    <Card.Header>
                      <h6 className="tw-m-0">
                        <i className="bi bi-info-circle tw-mr-2"></i>
                        Road Asset Information
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        {selectedAsset.length && (
                          <Col md={4}>
                            <strong>Road Length:</strong>
                            <br />
                            <span className="tw-text-primary">{selectedAsset.length} km</span>
                          </Col>
                        )}
                        {selectedAsset.summerClass && (
                          <Col md={4}>
                            <strong>Summer Classification:</strong>
                            <br />
                            <span className="tw-text-success">{selectedAsset.summerClass}</span>
                          </Col>
                        )}
                        {selectedAsset.winterClass && (
                          <Col md={4}>
                            <strong>Winter Classification:</strong>
                            <br />
                            <span className="tw-text-warning-text">{selectedAsset.winterClass}</span>
                          </Col>
                        )}
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            );
          }
          return null;
        })()}

        <div className="tw-mb-3 tw-flex tw-gap-2">
          <Button
            variant={showMap ? 'primary' : 'outline-primary'}
            onClick={(e) => {
              e.preventDefault();
              setShowMap(!showMap);
            }}
            disabled={isTaskLocked && !(task?.status === TaskStage.CLOSED || task?.status === TaskStage.FINALIZED)}
          >
            {showMap ? 'Hide Map' : 'Use Map'}
          </Button>
          {(() => {
            const selectedAsset = assets.find(a => a.id === formData.assetId);
            const hasAssetGps = selectedAsset && selectedAsset.startLatitude && selectedAsset.startLongitude;
            if (!hasAssetGps) return null;
            return (
              <Button
                variant="outline-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  const gpsUpdates: Partial<typeof formData> = {
                    startLatitude: selectedAsset.startLatitude,
                    startLongitude: selectedAsset.startLongitude,
                  };
                  if (formData.assetType?.toLowerCase() === 'road' && selectedAsset.endLatitude && selectedAsset.endLongitude) {
                    gpsUpdates.endLatitude = selectedAsset.endLatitude;
                    gpsUpdates.endLongitude = selectedAsset.endLongitude;
                  }
                  setFormData(prev => ({ ...prev, ...gpsUpdates }));
                }}
                disabled={isTaskLocked}
              >
                Use Asset GPS
              </Button>
            );
          })()}
        </div>

        {!treatAsPointActivity && (
          <>
            <div className="tw-mb-4">
              <RequiredLabel text="Starting Location" />
              <Row>
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>Lat:</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="any"
                      name="startLatitude"
                      id={taskFieldId('startLocation')}
                      isInvalid={!!fieldErrors.startLocation}
                      aria-describedby={fieldErrors.startLocation ? `${taskFieldId('startLocation')}-error` : undefined}
                      value={formData.startLatitude || ''}
                      onChange={handleChange}
                      placeholder="Start Latitude"
                      required
                      readOnly={isTaskLocked}
                      style={isTaskLocked ? lockedFieldStyle : undefined}
                    />
                  </InputGroup>
                </Col>
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>Lng:</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="any"
                      name="startLongitude"
                      isInvalid={!!fieldErrors.startLocation}
                      value={formData.startLongitude || ''}
                      onChange={handleChange}
                      placeholder="Start Longitude"
                      required
                      readOnly={isTaskLocked}
                      style={isTaskLocked ? lockedFieldStyle : undefined}
                    />
                  </InputGroup>
                </Col>
              </Row>
              {renderFieldError('startLocation')}
            </div>

            <div className="tw-mb-4">
              <Form.Label>Ending Location (Optional)</Form.Label>
              <Row>
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>Lat:</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="any"
                      name="endLatitude"
                      value={formData.endLatitude || ''}
                      onChange={handleChange}
                      placeholder="End Latitude"
                      readOnly={isTaskLocked}
                      style={isTaskLocked ? lockedFieldStyle : undefined}
                    />
                  </InputGroup>
                </Col>
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>Lng:</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="any"
                      name="endLongitude"
                      value={formData.endLongitude || ''}
                      onChange={handleChange}
                      placeholder="End Longitude"
                      readOnly={isTaskLocked}
                      style={isTaskLocked ? lockedFieldStyle : undefined}
                    />
                  </InputGroup>
                </Col>
              </Row>
            </div>
          </>
        )}

        {treatAsPointActivity && (
          <div className="tw-mb-4">
            <RequiredLabel text="Location" />
            {isSignAsset && (
              <Alert variant="info">
                <i className="bi bi-info-circle tw-mr-2"></i>
                Sign assets use a single location point.
              </Alert>
            )}
            <Row>
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text>Lat:</InputGroup.Text>
                  <Form.Control
                    type="number"
                    step="any"
                    name="startLatitude"
                    id={taskFieldId('startLocation')}
                    isInvalid={!!fieldErrors.startLocation}
                    aria-describedby={fieldErrors.startLocation ? `${taskFieldId('startLocation')}-error` : undefined}
                    value={formData.startLatitude || ''}
                    onChange={handleChange}
                    placeholder="Latitude"
                    required
                    readOnly={isTaskLocked}
                    style={isTaskLocked ? lockedFieldStyle : undefined}
                  />
                </InputGroup>
              </Col>
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text>Lng:</InputGroup.Text>
                  <Form.Control
                    type="number"
                    step="any"
                    name="startLongitude"
                    isInvalid={!!fieldErrors.startLocation}
                    value={formData.startLongitude || ''}
                    onChange={handleChange}
                    placeholder="Longitude"
                    required
                    readOnly={isTaskLocked}
                    style={isTaskLocked ? lockedFieldStyle : undefined}
                  />
                </InputGroup>
              </Col>
            </Row>
            {renderFieldError('startLocation')}
          </div>
        )}

        {/* Road Distance Information - only show for road assets (linear assets) */}
        {(() => {
          // Hide Road Distance Information section when task is Closed or Finalized
          const isTaskClosedOrFinalized = task?.status === TaskStage.CLOSED || task?.status === TaskStage.FINALIZED;
          if (isTaskClosedOrFinalized) {
            return null;
          }

          // Get the selected activity to check if it's a point type
          const selectedActivity = activities.find(a => a.id === formData.activityId);
          const isPointActivity = selectedActivity?.point === true && selectedActivity?.linear === false;
          const isSignAsset = formData.assetType?.toLowerCase() === 'sign';
          const treatAsPointActivity = isPointActivity || isSignAsset;

          // Get the selected asset to access its length
          const selectedAsset = assets.find(a => a.id === formData.assetId);

          // Only show DistanceDisplay for road assets (linear assets)
          const isRoadAsset = formData.assetType?.toLowerCase() === 'road';
          const hasValidCoordinates = formData.startLatitude && formData.startLongitude && formData.endLatitude && formData.endLongitude;

          return !treatAsPointActivity && isRoadAsset && hasValidCoordinates && (
            <DistanceDisplay
              startLat={formData.startLatitude ? parseFloat(String(formData.startLatitude)) : null}
              startLng={formData.startLongitude ? parseFloat(String(formData.startLongitude)) : null}
              endLat={formData.endLatitude ? parseFloat(String(formData.endLatitude)) : null}
              endLng={formData.endLongitude ? parseFloat(String(formData.endLongitude)) : null}
              onApplyDistance={(distance: number) => {
                setFormData(prev => ({
                  ...prev,
                  identifiedQuantity: distance.toString()
                }));
              }}
              disabled={isTaskLocked}
              assetLength={selectedAsset?.length}
              unitOfMeasure={formData.unitOfMeasure}
              overrideDistance={selectedRouteInfo?.distance}
              overrideDuration={selectedRouteInfo?.duration}
            />
          );
        })()}

        {showMap && (
          <>
            <GoogleGPSMap
              startLocation={formData.startLatitude && formData.startLongitude ? {
                lat: Number(formData.startLatitude),
                lng: Number(formData.startLongitude)
              } : undefined}
              endLocation={formData.endLatitude && formData.endLongitude ? {
                lat: Number(formData.endLatitude),
                lng: Number(formData.endLongitude)
              } : undefined}
              onLocationSelect={handleLocationSelect}
              locationType={locationType}
              isPointActivity={treatAsPointActivity}
              autoFitBounds={false}
              readOnly={task?.status === TaskStage.CLOSED || task?.status === TaskStage.FINALIZED}
              onRouteSelect={(distanceKm, durationMin) => setSelectedRouteInfo({ distance: distanceKm, duration: durationMin })}
            />
          </>
        )}
      </>
    );
  };

  const renderPlannedAccomplishment = () => {
    // Check if we need to make fields read-only
    const isReadOnly = task && (!hasPermission('quantified:accomplishment_edit') || isReadOnlyForFinanceReporter);
    const readOnlyStyle = isReadOnly ? {
      backgroundColor: '#e9ecef',
      cursor: 'not-allowed'
    } : undefined;

    return (
      <>
        <div className="tw-mb-3">
          {activitiesLoading ? (
            <div>
              <RequiredLabel text="Activity" />
              <div className="tw-block tw-w-full tw-rounded tw-border tw-border-line-input tw-bg-surface tw-text-content" style={{ padding: '0.375rem 0.75rem' }}>
                <Spinner as="span" animation="border" size="sm" className="tw-mr-2" role="status" aria-hidden="true" />
                Loading activities...
              </div>
            </div>
          ) : (
            <>
              <RequiredLabel text="Activity" />
              <div id={taskFieldId('activityId')}>
              <SearchableDropdown
                options={filteredActivities}
                value={formData.activityId}
                onChange={handleActivitySelection}
                getValue={(activity) => activity.id}
                formatOption={(activity) => `#${activity.activityNumber} - ${activity.activityName}`}
                disabled={
                  !formData.contractYear ||
                  !formData.foremanArea ||
                  activitiesLoading ||
                  isTaskLocked ||
                  isReadOnly
                }
                readOnly={isTaskLocked || isReadOnly}
                readOnlyStyle={isTaskLocked || isReadOnly ? lockedFieldStyle : undefined}
                required
                placeholder="Select Activity"
                noOptionsMessage={
                  !formData.contractYear
                    ? 'Please select a Contract Year first'
                    : !formData.foremanArea
                    ? 'Please select a Foreman Area first'
                    : 'No activities found for the selected Contract Year and Service Area'
                }
              />
              </div>
              {renderFieldError('activityId')}
            </>
          )}
        </div>

        <Row className="tw-mb-3">
          <Col md={6}>
            <RequiredLabel text="Planned Quantity" />
            <Form.Control
              type="number"
              step="any"
              min="0"
              name="identifiedQuantity"
              id={taskFieldId('identifiedQuantity')}
              isInvalid={!!fieldErrors.identifiedQuantity}
              aria-describedby={fieldErrors.identifiedQuantity ? `${taskFieldId('identifiedQuantity')}-error` : undefined}
              value={formData.identifiedQuantity || ''}
              onChange={handleChange}
              required
              readOnly={isTaskLocked || isReadOnly}
              style={isTaskLocked || isReadOnly ? lockedFieldStyle : undefined}
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === 'e') {
                  e.preventDefault();
                }
              }}
            />
            {renderFieldError('identifiedQuantity')}
          </Col>
          <Col md={6}>
            <RequiredLabel text="Unit of Measure" />
            <Form.Control
              type="text"
              name="unitOfMeasure"
              id={taskFieldId('unitOfMeasure')}
              isInvalid={!!fieldErrors.unitOfMeasure}
              aria-describedby={fieldErrors.unitOfMeasure ? `${taskFieldId('unitOfMeasure')}-error` : undefined}
              value={formData.unitOfMeasure || ''}
              readOnly
              style={readOnlyStyle}
            />
            {renderFieldError('unitOfMeasure')}
          </Col>
        </Row>



        <div className="tw-mb-3">
          <RequiredLabel text="Task Type" />
          <div className="tw-flex tw-w-full" role="group">
            <input
              type="radio"
              className="tw-sr-only"
              name="taskType"
              id="unitRate"
              value="Unit Rate"
              checked={formData.taskType === 'Unit Rate'}
              onChange={(e) => handleTaskTypeChange(e.target.value)}
              autoComplete="off"
              disabled={isTaskLocked || isReadOnly}
            />
            <label
              className={`tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-border tw-border-primary tw-px-3 tw-py-1.5 tw-cursor-pointer tw-rounded-l tw-rounded-r-none ${formData.taskType === 'Unit Rate' ? 'tw-bg-primary tw-text-white' : 'tw-bg-transparent tw-text-primary'}`}
              htmlFor="unitRate"
              style={isTaskLocked || isReadOnly ? getLockedActiveButtonStyle(true) : undefined}
            >
              Unit Rate
            </label>

            <input
              type="radio"
              className="tw-sr-only"
              name="taskType"
              id="lumpSum"
              value="Lump Sum"
              checked={formData.taskType === 'Lump Sum'}
              onChange={(e) => handleTaskTypeChange(e.target.value)}
              autoComplete="off"
              disabled={isTaskLocked || isReadOnly}
            />
            <label
              className={`tw-flex-1 -tw-ml-px tw-inline-flex tw-items-center tw-justify-center tw-border tw-border-primary tw-px-3 tw-py-1.5 tw-cursor-pointer tw-rounded-r tw-rounded-l-none ${formData.taskType === 'Lump Sum' ? 'tw-bg-primary tw-text-white' : 'tw-bg-transparent tw-text-primary'}`}
              htmlFor="lumpSum"
              style={isTaskLocked || isReadOnly ? getLockedActiveButtonStyle(true) : undefined}
            >
              Lump Sum
            </label>
          </div>
        </div>

        {formData.taskType === 'Lump Sum' && (
          <>
            <div className="tw-mb-3">
              <RequiredLabel text="Lump Sum Costing" />
              <div className="tw-flex tw-w-full" role="group">
                <input
                  type="radio"
                  className="tw-sr-only"
                  name="costType"
                  id="fixedCost"
                  value="Fixed Cost"
                  checked={formData.costType === 'Fixed Cost'}
                  onChange={handleChange}
                  autoComplete="off"
                  disabled={isTaskLocked || isReadOnly}
                />
                <label
                  className={`tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-border tw-border-primary tw-px-3 tw-py-1.5 tw-cursor-pointer tw-rounded-l tw-rounded-r-none ${formData.costType === 'Fixed Cost' ? 'tw-bg-primary tw-text-white' : 'tw-bg-transparent tw-text-primary'}`}
                  htmlFor="fixedCost"
                  style={isTaskLocked || isReadOnly ? getLockedActiveButtonStyle(true) : undefined}
                >
                  Fixed Cost
                </label>

                <input
                  type="radio"
                  className="tw-sr-only"
                  name="costType"
                  id="costPlus"
                  value="Cost Plus"
                  checked={formData.costType === 'Cost Plus'}
                  onChange={handleChange}
                  autoComplete="off"
                  disabled={isTaskLocked || isReadOnly}
                />
                <label
                  className={`tw-flex-1 -tw-ml-px tw-inline-flex tw-items-center tw-justify-center tw-border tw-border-primary tw-px-3 tw-py-1.5 tw-cursor-pointer tw-rounded-r tw-rounded-l-none ${formData.costType === 'Cost Plus' ? 'tw-bg-primary tw-text-white' : 'tw-bg-transparent tw-text-primary'}`}
                  htmlFor="costPlus"
                  style={isTaskLocked || isReadOnly ? getLockedActiveButtonStyle(true) : undefined}
                >
                  Cost Plus
                </label>
              </div>
            </div>

            {formData.costType === 'Fixed Cost' && (
              <div className="tw-mb-3">
                <RequiredLabel text="Total Expected Value" />
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="total"
                    id={taskFieldId('total')}
                    isInvalid={!!fieldErrors.total}
                    aria-describedby={fieldErrors.total ? `${taskFieldId('total')}-error` : undefined}
                    value={formData.total || ''}
                    onChange={handleChange}
                    placeholder="Enter total value"
                    required
                    readOnly={isTaskLocked || isReadOnly}
                    style={isTaskLocked || isReadOnly ? lockedFieldStyle : undefined}
                  />
                </InputGroup>
                {renderFieldError('total')}
                <small className="tw-text-content-muted">
                  Enter the total value directly (e.g. 1000)
                </small>
              </div>
            )}
          </>
        )}

        {formData.taskType === 'Unit Rate' && (
          <>
            <hr className="tw-my-4" />
            <h6 className="tw-mb-3">Rate Information</h6>

            {/* Base Rate and Agreed Adjusted Rate in two columns */}
            <Row className="tw-mb-3">
              <Col md={6}>
                <Form.Label>Base Rate</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="baseRate"
                    id={taskFieldId('baseRate')}
                    isInvalid={!!fieldErrors.baseRate}
                    aria-describedby={fieldErrors.baseRate ? `${taskFieldId('baseRate')}-error` : undefined}
                    value={formData.baseRate || ''}
                    onChange={handleChange}
                    placeholder="Enter base rate"
                    readOnly
                    style={readOnlyStyle}
                  />
                </InputGroup>
                {renderFieldError('baseRate')}
              </Col>
              <Col md={6}>
                <Form.Label>Agreed Adjusted Rate (Optional)</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="rateAdjustment"
                    value={formData.rateAdjustment || ''}
                    onChange={handleChange}
                    placeholder="Enter adjusted rate"
                    readOnly={isTaskLocked || isReadOnly}
                    style={isTaskLocked || isReadOnly ? lockedFieldStyle : undefined}
                  />
                </InputGroup>
                <small className="tw-text-content-muted">
                  This will override the base rate if provided. Enter numeric value only (e.g. 100.50)
                </small>
              </Col>
            </Row>

            {/* Agreed Quantity Multiplier and Total Expected Value in two columns */}
            <Row className="tw-mb-3">
              <Col md={6}>
                <Form.Label>Agreed Quantity Multiplier (Optional)</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    name="rateMultiplier"
                    value={formData.rateMultiplier || ''}
                    onChange={handleChange}
                    placeholder="Enter multiplier (default: 1.00)"
                    readOnly={isTaskLocked || isReadOnly}
                    style={isTaskLocked || isReadOnly ? lockedFieldStyle : undefined}
                  />
                  <InputGroup.Text>×</InputGroup.Text>
                </InputGroup>
                <small className="tw-text-content-muted">
                  Enter numeric value only (e.g. 1.5 for 150%)
                </small>
              </Col>
              <Col md={6}>
                <Form.Label>Total Expected Value</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={formData.total || ''}
                    readOnly
                    style={readOnlyStyle}
                  />
                </InputGroup>
                <small className="tw-text-content-muted">
                  Calculated as: (Base Rate or Adjusted Rate) × Multiplier × Quantity
                </small>
              </Col>
            </Row>

            {shouldShowRateComments(formData, activities.find(a => a.id === formData.activityId)) && (
              <div className="tw-mb-3">
                <Alert variant="warning">
                  <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                  Rate modifications detected. Comments are required.
                </Alert>
                <RequiredLabel text="Rate Adjustment Comments" />
                <Form.Control
                  as="textarea"
                  className={!formData.rateAdjustmentComments?.trim() ? 'tw-border-danger' : ''}
                  name="rateAdjustmentComments"
                  value={formData.rateAdjustmentComments || ''}
                  onChange={handleChange}
                  readOnly={isTaskLocked || isReadOnly}
                  style={isTaskLocked || isReadOnly ? lockedFieldStyle : undefined}
                />
              </div>
            )}
          </>
        )}

        {formData.taskType === 'Lump Sum' && formData.costType === 'Cost Plus' && (
          <div className="markup-section">
            <h6 className="tw-mb-3">Markup Percentages</h6>

            <Row>
              <Col md={6} className="tw-mb-3">
                <Form.Label>Labour Markup</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    name="labourMarkup"
                    value={formData.labourMarkup || ''}
                    onChange={handleChange}
                    readOnly={isTaskLocked || isReadOnly}
                    style={isTaskLocked || isReadOnly ? lockedFieldStyle : undefined}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              </Col>

              <Col md={6} className="tw-mb-3">
                <Form.Label>Material Markup</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    name="materialMarkup"
                    value={formData.materialMarkup || ''}
                    onChange={handleChange}
                    readOnly={isTaskLocked || isReadOnly}
                    style={isTaskLocked || isReadOnly ? lockedFieldStyle : undefined}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              </Col>

              <Col md={6} className="tw-mb-3">
                <Form.Label>Subcontractor Markup</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    name="subcontractorMarkup"
                    value={formData.subcontractorMarkup || ''}
                    onChange={handleChange}
                    readOnly={isTaskLocked || isReadOnly}
                    style={isTaskLocked || isReadOnly ? lockedFieldStyle : undefined}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              </Col>

              <Col md={6} className="tw-mb-3">
                <Form.Label>Hired Equipment Markup</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    name="hiredEquipmentMarkup"
                    value={formData.hiredEquipmentMarkup || ''}
                    onChange={handleChange}
                    readOnly={isTaskLocked || isReadOnly}
                    style={isTaskLocked || isReadOnly ? lockedFieldStyle : undefined}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              </Col>

              <Col md={6} className="tw-mb-3">
                <Form.Label>Vehicle Markup</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    name="vehicleMarkup"
                    value={formData.vehicleMarkup || ''}
                    onChange={handleChange}
                    readOnly={isTaskLocked || isReadOnly}
                    style={isTaskLocked || isReadOnly ? lockedFieldStyle : undefined}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              </Col>
            </Row>
          </div>
        )}
      </>
    );
  };

  const renderActualAccomplishment = () => {
    // Check if user can edit actual accomplishment (only Ops Managers in Internal Review)
    const canEditActualAccomplishment = task?.status === TaskStage.INTERNAL_REVIEW && 
      hasPermission('quantified:actual_accomplishment_edit');
    
    const isReadOnly = !canEditActualAccomplishment;
    const readOnlyStyle = isReadOnly ? {
      backgroundColor: '#e9ecef',
      cursor: 'not-allowed'
    } : undefined;

    const taskCosting = task?.taskCostings?.[0];
    const isInternalReview = task?.status === TaskStage.INTERNAL_REVIEW;
    const isLumpSum = taskCosting?.taskType === 'Lump Sum';
    const isFixedCost = taskCosting?.costType === 'Fixed Cost';
    const isCostPlus = taskCosting?.costType === 'Cost Plus';
    const canEditActualTotal = isInternalReview && hasPermission('quantified:actual_accomplishment_edit') && isFixedCost;

    return (
      <>
        <Alert variant="info">
          <i className="bi bi-info-circle tw-mr-2"></i>
          This tab shows the actual completion data for the task.
          {task?.status === TaskStage.INTERNAL_REVIEW && hasPermission('quantified:actual_accomplishment_edit') && (
            <span className="tw-block tw-mt-1">
              <strong>Note:</strong> You can edit the actual accomplishment, start date, and completion date while the task is in Internal Review.
            </span>
          )}
        </Alert>

        <Row className="tw-mb-3">
          <Col md={6}>
            <Form.Label>Actual Accomplishment</Form.Label>
            <Form.Control
              type="number"
              step="any"
              min={hasPermission('quantified:zero_accomplishment') ? "0" : "0.01"}
              name="completionAccomplishment"
              value={formData.completionAccomplishment || ''}
              onChange={handleChange}
              readOnly={isReadOnly}
              style={isReadOnly ? readOnlyStyle : undefined}
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === 'e') {
                  e.preventDefault();
                }
              }}
            />
            {!hasPermission('quantified:zero_accomplishment') && (
              <small className="tw-text-content-muted">
                Minimum value: 0.01 (contact administrator for 0 accomplishment permission)
              </small>
            )}
          </Col>
          <Col md={6}>
            <Form.Label>Unit of Measure</Form.Label>
            <Form.Control
              type="text"
              name="unitOfMeasure"
              value={formData.unitOfMeasure || ''}
              readOnly
              style={readOnlyStyle}
            />
          </Col>
        </Row>

        <Row className="tw-mb-3">
          <Col md={6}>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              name="completionStartDate"
              value={formData.completionStartDate || (() => {
                // Calculate start date from earliest effort or use planned start date as fallback
                if (!task) return '';
                const earliestEffortDate = getEarliestEffortDate(task);
                if (earliestEffortDate) {
                  return earliestEffortDate.toISOString().split('T')[0];
                } else if (task.identifiedDate) {
                  return new Date(task.identifiedDate).toISOString().split('T')[0];
                }
                return '';
              })()}
              onChange={handleChange}
              readOnly={isReadOnly}
              style={isReadOnly ? readOnlyStyle : undefined}
            />
            <small className="tw-text-content-muted">
              {(() => {
                if (!task) return 'No start date available';
                if (canEditActualAccomplishment) {
                  return 'You can edit the start date for actual accomplishment';
                }
                const earliestEffortDate = getEarliestEffortDate(task);
                if (earliestEffortDate) {
                  return `Calculated from earliest effort date`;
                } else if (task.identifiedDate) {
                  return `Using Planned Start Date (no efforts found)`;
                }
                return 'No start date available';
              })()}
            </small>
          </Col>
          <Col md={6}>
            <Form.Label>Completion Date</Form.Label>
            <Form.Control
              type="date"
              name="completionDate"
              value={formData.completionDate || ''}
              onChange={handleChange}
              readOnly={isReadOnly}
              style={isReadOnly ? readOnlyStyle : undefined}
            />
          </Col>
        </Row>

        {/* Actual Total Value */}
        <div className="tw-mb-3">
          <Form.Label>Actual Total Value</Form.Label>
          <InputGroup>
            <InputGroup.Text>$</InputGroup.Text>
            <Form.Control
              type="text"
              value={(() => {
                const lifecycleStatusEarly = formData.status || task?.status;
                if (
                  lifecycleStatusEarly &&
                  shouldForceActualTotalValueToZero(lifecycleStatusEarly)
                ) {
                  return '0.00';
                }

                // For Fixed Cost tasks where user can edit, use the input value if set
                if (canEditActualTotal && actualTotalValueInput !== undefined && actualTotalValueInput !== '') {
                  return actualTotalValueInput;
                }
                
                // For all other cases (Unit Rate or Fixed Cost without manual edit), calculate in real-time
                const completionAccomplishment = Number(formData.completionAccomplishment) || 0;
                
                // Construct a task object with current formData values for real-time calculation
                let taskForCalculation = task;
                if (task && taskCosting) {
                  // Create a task object with updated taskCosting from formData for real-time calculation
                  taskForCalculation = {
                    ...task,
                    taskCostings: [{
                      ...taskCosting,
                      baseRate: Number(formData.baseRate) || taskCosting.baseRate || 0,
                      rateMultiplier: Number(formData.rateMultiplier) || taskCosting.rateMultiplier || 1,
                      rateAdjustment: Number(formData.rateAdjustment) || taskCosting.rateAdjustment || 0
                    }]
                  };
                }
                
                return calculateActualTotalValue(taskForCalculation, completionAccomplishment).toFixed(2);
              })()}
              readOnly={!canEditActualTotal}
              style={!canEditActualTotal ? readOnlyStyle : undefined}
              onChange={e => {
                if (canEditActualTotal) {
                  setActualTotalValueInput(e.target.value);
                  // Mark as manually edited when user types
                  setIsActualTotalValueManuallyEdited(true);
                }
              }}
              onBlur={e => {
                if (canEditActualTotal && e.target.value) {
                  // Validate that it's a valid number
                  const numValue = Number(e.target.value);
                  if (!isNaN(numValue) && numValue >= 0) {
                    setActualTotalValueInput(numValue.toFixed(2));
                    // Keep the manually edited flag
                    setIsActualTotalValueManuallyEdited(true);
                  }
                }
              }}
            />
          </InputGroup>
          <small className="tw-text-content-muted">
            {(() => {
              const ls = formData.status || task?.status;
              if (ls && shouldForceActualTotalValueToZero(ls)) {
                return 'Until Internal Review, Closed, or Finalized, actual total value is kept at $0; planned revenue is Total Value above.';
              }
              if (!taskCosting) return 'No task costing found.';
              if (taskCosting.taskType === 'Unit Rate') {
                return 'This value is calculated automatically based on the completion accomplishment and cannot be edited. If manually overridden, the backend will use the provided value; otherwise, it uses the Unit Rate calculation (rate × multiplier × accomplishment).';
              } else if (taskCosting.taskType === 'Lump Sum') {
                if (taskCosting.costType === 'Fixed Cost') {
                  return isInternalReview 
                    ? 'You can edit this value while in Internal Review. If manually overridden, the backend will use the provided value; otherwise, it uses the expected total value for Fixed Cost tasks.' 
                    : 'This value is the same as the expected value for fixed cost tasks. If manually overridden, the backend will use the provided value; otherwise, it uses the expected total value.';
                } else if (taskCosting.costType === 'Cost Plus') {
                  return isInternalReview 
                    ? 'You can edit this value while in Internal Review. If manually overridden, the backend will use the provided value; otherwise, it calculates from efforts with markups (Cost Plus calculation).' 
                    : 'This value is calculated from efforts. If manually overridden, the backend will use the provided value; otherwise, it calculates from efforts with markups (Cost Plus calculation).';
                }
              }
              return '';
            })()}
          </small>
        </div>

        {canEditActualAccomplishment && (
          <Alert variant="warning">
            <i className="bi bi-exclamation-triangle tw-mr-2"></i>
            <strong>Warning:</strong> Changes to actual accomplishment will be logged in the task history.
          </Alert>
        )}


      </>
    );
  };

  const renderAttachments = () => {
    // Separate image attachments from other attachments
    const imageAttachments = attachments.filter(attachment => 
      attachment.attachmentType === 'image' || 
      attachment.attachmentName.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)
    );
    const otherAttachments = attachments.filter(attachment => 
      attachment.attachmentType !== 'image' && 
      !attachment.attachmentName.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)
    );

    return (
      <>
        {attachmentError && (
          <Alert variant="danger" role="alert" dismissible onClose={() => setAttachmentError(null)}>
            {attachmentError}
          </Alert>
        )}
        <div className="tw-mb-3">
          <Form.Label>Upload New Attachment</Form.Label>
          <div className="attachment-upload">
            <Form.Control
              type="file"
              className="tw-mb-2"
              onChange={handleFileUpload}
              disabled={uploading || !task?.id || isTaskLocked}
              style={isTaskLocked ? lockedFieldStyle : undefined}
            />
            {pendingFile && (
              <div className="tw-mb-3">
                <Form.Control
                  as="textarea"
                  placeholder="Add a note for this attachment (optional)..."
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleNoteSave(e.currentTarget.value);
                    }
                  }}
                  disabled={uploading}
                />
                <div className="tw-flex tw-gap-2 tw-mt-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => {
                      const textarea = e.currentTarget.parentElement?.previousElementSibling as HTMLTextAreaElement;
                      handleNoteSave(textarea.value);
                    }}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setPendingFile(null);
                      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
                      if (fileInput) {
                        fileInput.value = '';
                      }
                    }}
                    disabled={uploading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {uploading && (
              <div className="tw-text-content-muted tw-mt-2">
                <i className="bi bi-arrow-clockwise animation-spin tw-mr-2"></i>
                Uploading...
              </div>
            )}
          </div>
        </div>

        {/* Show image attachments for quantified tasks */}
        {task?.type === 'Quantified' && imageAttachments.length > 0 && (
          <TaskImageAttachments 
            attachments={imageAttachments}
            onDeleteAttachment={handleDeleteAttachment}
            confirmBeforeDelete={false}
            isTaskClosed={isTaskLocked}
          />
        )}

        {/* Show other attachments */}
        <div className="tw-mt-4">
          <h6>Other Attachments</h6>
          {otherAttachments.length === 0 ? (
            <div className="tw-text-content-muted">No other attachments found</div>
          ) : (
            <ListGroup>
              {Array.isArray(otherAttachments) && otherAttachments.map((attachment) => (
                <ListGroup.Item key={attachment.id} className="tw-flex tw-justify-between tw-items-center">
                  <div>
                    <a
                      href={attachment.attachmentURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-no-underline"
                    >
                      <i className="bi bi-file-earmark tw-mr-2"></i>
                      {attachment.attachmentName}
                    </a>
                    {attachment.attachmentNotes && (
                      <small className="tw-block tw-text-content-muted tw-mt-1">{attachment.attachmentNotes}</small>
                    )}
                  </div>
                  {!isTaskLocked && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteAttachment(attachment.id ?? 0)}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>
      </>
    );
  };

  // Add component render logging
  useEffect(() => {
  }, []);

  // Helper function to find the selected asset
  const findSelectedAsset = () => {
    if (!formData.assetId) return null;
    
    // First check in the assets array
    const selectedAsset = assets.find(a => a.id === formData.assetId);
    if (selectedAsset) {
      return selectedAsset;
    }
    
    // If not found, check in grouped assets
    for (const key in groupedAssets) {
      const foundInGroup = groupedAssets[key].find(a => a.id === formData.assetId);
      if (foundInGroup) {
        return foundInGroup;
      }
    }
    
    return null;
  };
  
  const convertContractAreasForForeman = (
    areas: Awaited<ReturnType<typeof areasService.getContractAreas>>,
    foremanAreaId: number
  ): ContractArea[] =>
    (Array.isArray(areas) ? areas : []).map((area) => ({
      id: area.id,
      name: area.name,
      code: area.code || '',
      foremanAreaId: foremanAreaId,
      foremanArea: {
        id: foremanAreaId,
        name: '',
        code: '',
        serviceAreaId: 0,
      },
    })) as unknown as ContractArea[];

  /** Load contract areas scoped to a foreman area (Location tab + Work ID flow). */
  const fetchContractAreas = useCallback(async (foremanAreaId: number): Promise<ContractArea[]> => {
    try {
      const areas = await areasService.getContractAreas(foremanAreaId);

      if (!Array.isArray(areas)) {
        console.warn('AreasService.getContractAreas returned non-array response:', areas);
        setContractAreas([]);
        return [];
      }

      const convertedAreas = convertContractAreasForForeman(areas, foremanAreaId);
      setContractAreas(convertedAreas);
      return convertedAreas;
    } catch (error) {
      console.error('Error fetching contract areas:', error);
      setContractAreas([]);
      return [];
    }
  }, []);

  // Add handler for contract area change
  const handleContractAreaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newContractArea = e.target.value;
    setFormData(prev => ({
      ...prev,
      contractArea: newContractArea,
      assetType: undefined,
      assetId: null
    }));

    // Clear assets when contract area changes
    setAssets([]);
    setRoadAssets([]);
  };

  // Handle foreman area change and auto-select first contract area
  const handleForemanAreaChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    
    const value = e.target.value;
    
    // Get default markups for the selected foreman area
    const selectedForemanArea = foremanAreas.find(area => area.id === Number(value));
    const serviceAreaId = selectedForemanArea?.serviceArea?.id.toString();
    const defaultMarkups = serviceAreaId ? SERVICE_AREA_MARKUPS[serviceAreaId as keyof typeof SERVICE_AREA_MARKUPS] : null;
    
    // First update the foreman area and clear contract area
    setFormData(prev => ({
      ...prev,
      foremanArea: value,
      contractArea: '', // Clear contract area selection initially
      assetType: undefined,
      assetId: null,
      // Only update markups if cost type is Cost Plus
      ...(defaultMarkups && prev.costType === 'Cost Plus' && {
        labourMarkup: defaultMarkups.labourMarkup,
        materialMarkup: defaultMarkups.materialMarkup,
        vehicleMarkup: defaultMarkups.vehicleMarkup,
        subcontractorMarkup: defaultMarkups.subcontractorMarkup,
        hiredEquipmentMarkup: defaultMarkups.hiredEquipmentMarkup
      })
    }));

    if (value && Number(value) > 0) {
      const convertedAreas = await fetchContractAreas(Number(value));

      if (convertedAreas.length > 0) {
        const selectedForemanAreaName = selectedForemanArea?.name || '';
        const matchingContractArea = convertedAreas.find(
          (area) => area.name.toLowerCase() === selectedForemanAreaName.toLowerCase()
        );
        const defaultContractArea = matchingContractArea || convertedAreas[0];
        const defaultContractAreaId = defaultContractArea.id.toString();

        setFormData((prev) => ({
          ...prev,
          contractArea: defaultContractAreaId,
        }));
      }
    } else {
      setContractAreas([]);
    }
  };

  // Reset active tab to 'basic' whenever the modal opens
  useEffect(() => {
    if (visible) {
      setActiveTab('basic');
    }
  }, [visible]);

  // Add Esc key listener to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && visible) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, onClose]);

  // Contract areas scoped to selected foreman area (or Work ID initialContractAreas prop)
  useEffect(() => {
    if (!visible) {
      return;
    }

    const foremanId = formData.foremanArea ? Number(formData.foremanArea) : NaN;
    if (Number.isFinite(foremanId) && foremanId > 0) {
      fetchContractAreas(foremanId);
      return;
    }

    if (initialContractAreas.length > 0) {
      setContractAreas(initialContractAreas);
      return;
    }

    setContractAreas([]);
  }, [visible, formData.foremanArea, initialContractAreas, fetchContractAreas]);

  // Add new effect to reset form data when modal visibility changes
  useEffect(() => {
    
    if (!visible) {
      // Reset form data when modal is closed
      setFormData(initialFormState);
      setSaveError(null);
      setFieldErrors({});
      fieldErrorSnapshotRef.current = {};
      setAttachmentError(null);
      setProjectsLoadError(false);
      setActivities([]);
      setActivitiesLoading(false);
      activitiesFetchSeqRef.current += 1;
      isTaskNameManuallyEditedRef.current = false;
      contractYearDefaultAppliedRef.current = false;
      newTaskInitializedRef.current = false;
      workIdDataMergedRef.current = false;
      lastProcessedTaskIdRef.current = undefined; // Reset the ref when modal closes
      setActiveTab('basic');
      setShowMap(false);
      setLocationType('start');
      setPendingFile(null);
      setDeletingAttachmentId(null);
      setShowDeleteConfirm(false);
      setContractAreas([]);
    } else if (visible && !task) {
      if (!newTaskInitializedRef.current) {
        newTaskInitializedRef.current = true;

        let initialStatus = TaskStage.PROPOSED;
        if (defaultStatus) {
          const statusUpper = defaultStatus.toUpperCase();
          if (statusUpper === 'ACTIVE') {
            initialStatus = TaskStage.ACTIVE;
          } else if (statusUpper === 'PLANNED') {
            initialStatus = TaskStage.PLANNED;
          } else if (statusUpper === 'PROPOSED') {
            initialStatus = TaskStage.PROPOSED;
          }
        }

        const startLoc = initialDataFromWorkId?.taskLocations?.[0];

        setFormData(prev => {
          const newFormData = {
            ...initialFormState,
            status: initialStatus,
            project:
              prev.project ||
              (initialProject && initialProject.status?.toLowerCase() !== 'completed'
                ? initialProject.id.toString()
                : ''),
            contractYear: initialDataFromWorkId?.contractYear ?? '',
            identifiedDate: getCurrentLocalDateString(),
            ...(initialDataFromWorkId && {
              activityId: initialDataFromWorkId.activityId,
              unitOfMeasure: initialDataFromWorkId.unitOfMeasure ?? '',
              identifiedQuantity: initialDataFromWorkId.identifiedQuantity?.toString() ?? '',
              foremanArea: initialDataFromWorkId.foremanAreaId?.toString() ?? '',
              contractArea: initialDataFromWorkId.contractAreaId?.toString() ?? '',
              ...(initialDataFromWorkId.name && { name: initialDataFromWorkId.name }),
              ...(startLoc && {
                startLatitude: startLoc.latitude?.toString() ?? '',
                startLongitude: startLoc.longitude?.toString() ?? '',
                assetId: startLoc.assetId ?? null
              })
            })
          };
          return newFormData;
        });
        isTaskNameManuallyEditedRef.current = Boolean(initialDataFromWorkId?.name && String(initialDataFromWorkId.name).trim());
      }
    } else if (visible && task) {
      // Restore task data when reopening an existing task
      // Only run this if we haven't processed this task ID yet (when modal opens with a new task)
      const isNewTask = lastProcessedTaskIdRef.current !== task.id;
      
      
      // Only update if this is a different task (new task ID) - prevents resetting when task is refreshed after save
      if (!isNewTask) {
        return;
      }
      
      lastProcessedTaskIdRef.current = task.id;
      isTaskNameManuallyEditedRef.current = Boolean(task.name && String(task.name).trim());
      vehicleTypesSyncedKeyRef.current = '';

      const taskAssignedEmployeeIds = task.assignedEmployees?.map(emp => emp.id) || [];
      const taskEstimatedEffortHours = task.estimatedEffortHours?.toString() || '';
      const taskPlannedCompletionDate = task.plannedCompletionDate ? new Date(task.plannedCompletionDate).toISOString().split('T')[0] : '';
      const startLocation = task.taskLocations?.find(loc => loc.type === 'START');
      const endLocation = task.taskLocations?.find(loc => loc.type === 'END');
      const identifiedQuantity = task.quantifiedTasks?.[0]?.identifiedQuantity || 0;
      const baseRate = task.taskCostings?.[0]?.baseRate || 0;
      const rateMultiplier = task.taskCostings?.[0]?.rateMultiplier || 1;
      const rateAdjustment = task.taskCostings?.[0]?.rateAdjustment || 0;
      const parsedDependencies = extractScheduleDependenciesFromComments(task.comments || null);

      setFormData(prev => ({
        ...initialFormState,
        id: task.id,
        activityId: task.activities?.[0]?.id,
        description: task.description || '',
        instructions: task.instructions || '',
        name: task.name || '',
        priority: task.priority || 'LOW',
        status: task.status as TaskStage,
        taskType: task.taskCostings?.[0]?.taskType || 'Unit Rate',
        costType: task.taskCostings?.[0]?.costType || 'Fixed Cost',
        rateAdjustmentComments: task.taskCostings?.[0]?.rateAdjustmentComments || '',
        labourMarkup: task.taskCostings?.[0]?.labourMarkup || 0,
        materialMarkup: task.taskCostings?.[0]?.materialMarkup || 0,
        subcontractorMarkup: task.taskCostings?.[0]?.subcontractorMarkup || 0,
        vehicleMarkup: task.taskCostings?.[0]?.vehicleMarkup || 0,
        hiredEquipmentMarkup: task.taskCostings?.[0]?.hiredEquipmentMarkup || 0,
        project: task.projectId?.toString() || '',
        foremanArea: task.foremanAreaId?.toString() || '',
        contractArea: task.contractAreaId?.toString() || '',
        contractYear: task.contractYear || '',
        identifiedDate: task.identifiedDate ? new Date(task.identifiedDate).toISOString().split('T')[0] : '',
        completionDate: task.completionDate ? new Date(task.completionDate).toISOString().split('T')[0] : '',
        completionStartDate: (() => {
          // Use task.startDate if available, otherwise calculate from earliest effort
          if (task.startDate) {
            return new Date(task.startDate).toISOString().split('T')[0];
          }
          const earliestEffortDate = getEarliestEffortDate(task);
          if (earliestEffortDate) {
            return earliestEffortDate.toISOString().split('T')[0];
          }
          return task.identifiedDate ? new Date(task.identifiedDate).toISOString().split('T')[0] : '';
        })(),
        completionAccomplishment: task.completionAccomplishment?.toString() || '',
        identifiedQuantity: identifiedQuantity.toString(),
        unitOfMeasure: task.quantifiedTasks?.[0]?.unitOfMeasure || '',
        baseRate: baseRate.toString(),
        rateMultiplier: rateMultiplier,
        rateAdjustment: rateAdjustment.toString(),
        total: task.taskCostings?.[0]?.totalValue?.toString() || '',
        startLatitude: startLocation?.latitude?.toString() || '',
        startLongitude: startLocation?.longitude?.toString() || '',
        endLatitude: endLocation?.latitude?.toString() || '',
        endLongitude: endLocation?.longitude?.toString() || '',
        assetId: startLocation?.assetId || null,
        // Keep the existing assetType if it exists, otherwise it will be set by the loadAssetTypeAndAsset effect
        assetType: prev.assetType,
        comments: parsedDependencies.cleanComments || '',
        taskNumber: task.taskNumber || '',
        estimatedEffortHours: task.estimatedEffortHours?.toString() || '',
        plannedCompletionDate: task.plannedCompletionDate ? new Date(task.plannedCompletionDate).toISOString().split('T')[0] : '',
        assignedEmployeeIds: task.assignedEmployees?.map(emp => emp.id) || [],
        // Equipment types are inferred once schedule vehicles load (see sync effect).
        assignedVehicleTypes: [],
        dependencyTriggers: parsedDependencies.dependencies?.triggers || [],
        dependencyOtherComment: parsedDependencies.dependencies?.otherComment || '',
        dependencyResourceType: parsedDependencies.dependencies?.resourceDependentType || '',
        dependencyCompletionTaskId: parsedDependencies.dependencies?.completionTaskId ? String(parsedDependencies.dependencies.completionTaskId) : '',
      }));
      

      // Contract areas are loaded via the dedicated useEffect, no need to load them here
    }
  }, [visible, task, initialProject, defaultStatus, initialDataFromWorkId]);

  // Add this effect after the other useEffects
  useEffect(() => {
    const loadAssetTypeAndAsset = async () => {
      // Determine the assetId to look up: from existing task or from Work ID initial data
      const assetIdToLoad = task?.taskLocations?.[0]?.assetId
        ?? initialDataFromWorkId?.taskLocations?.[0]?.assetId
        ?? null;

      if (!assetIdToLoad) return;

      try {
        const asset = await assetService.getAssetById(assetIdToLoad);
        if (asset) {
          setFormData(prev => ({
            ...prev,
            assetType: asset.assetType?.toLowerCase() as AssetTypeEnum
          }));

          const contractAreaId = formData.contractArea
            || initialDataFromWorkId?.contractAreaId?.toString()
            || '';

          if (contractAreaId && asset.assetType) {
            await loadAssets(Number(contractAreaId), asset.assetType.toLowerCase() as AssetTypeEnum);
          }
        }
      } catch (error) {
        console.error('Error loading asset type and asset:', error);
      }
    };

    if (visible && (task || initialDataFromWorkId)) {
      loadAssetTypeAndAsset();
    }
  }, [visible, task, initialDataFromWorkId, formData.contractArea]);

  // Add a function to validate parent asset references
  const validateParentAssetReferences = (assets: Asset[], roadAssets: Asset[]) => {
    return assets.map(asset => {
      if (asset.parentAsset) {
        // Check if the parent asset exists and is actually a road
        const parentAsset = roadAssets.find(road => 
          road.assetNumber?.trim() === asset.parentAsset?.trim()
        );
        
        if (!parentAsset || parentAsset.assetType?.toLowerCase() !== 'road') {
          console.warn(`Invalid parent asset reference for ${asset.assetNumber}: ${asset.parentAsset}`);
          return {
            ...asset,
            parentAsset: undefined
          };
        }
      }
      return asset;
    });
  };

  // Add a debugging useEffect to monitor asset state
  useEffect(() => {
    // Only log when we have assets loaded and a non-road asset type selected
    if (assets.length > 0 && formData.assetType && formData.assetType.toLowerCase() !== 'road') {
      
      // Check a sample of assets
      const withParents = assets.filter(a => a.parentAsset);
      if (withParents.length > 0) {
      } else {
      }
      
      // Log road assets for debugging
      if (roadAssets.length > 0) {
      }
      
      // Log grouping results
      if (roadAssets.length > 0) {
        const grouped = groupAssetsByParent(assets, roadAssets);
      } else {
      }
    }
  }, [assets, roadAssets, formData.assetType]);

  // Add debugging for MultiTierDropdown assets
  useEffect(() => {
    if (assets.length > 0) {
    }
  }, [assets]);

  // Add this function to handle actual accomplishment updates (now used by main Update button)
  const updateActualAccomplishmentIfNeeded = async () => {
    if (!task?.id || !hasPermission('quantified:actual_accomplishment_edit') || task.status !== TaskStage.INTERNAL_REVIEW) {
      return null;
    }
    try {
      setLoading(true);
      // Use the existing updateActualAccomplishment method which now uses the merged route
      // Include actualTotalValue if it was manually edited (for Fixed Cost tasks)
      const taskCosting = task?.taskCostings?.[0];
      const isFixedCost = taskCosting?.taskType === 'Lump Sum' && taskCosting?.costType === 'Fixed Cost';
      const canEditActualTotal = task?.status === TaskStage.INTERNAL_REVIEW && 
        hasPermission('quantified:actual_accomplishment_edit') && isFixedCost;
      
      const updatePayload: any = {
        completionAccomplishment: Number(formData.completionAccomplishment) || 0,
        completionDate: formData.completionDate ? new Date(formData.completionDate) : undefined,
        startDate: formData.completionStartDate ? new Date(formData.completionStartDate) : undefined,
        userId: currentUser?.id
      };
      
      // If user manually edited actualTotalValue, include it with the manuallyEdited flag
      if (isActualTotalValueManuallyEdited && actualTotalValueInput !== undefined && actualTotalValueInput !== null && actualTotalValueInput !== '') {
        const numValue = Number(actualTotalValueInput);
        if (!isNaN(numValue)) {
          updatePayload.actualTotalValue = numValue;
          updatePayload.manuallyEdited = true;
        }
      }
      
      const updatedTask = await taskService.updateActualAccomplishment(task.id, updatePayload);
      
      // Refresh the task to get the latest history and all related data
      // This ensures all tabs see the updated task history
      let refreshedTask = updatedTask;
      try {
        refreshedTask = await taskService.getTask(task.id);
      } catch (error) {
        console.warn('Failed to refresh task after actual accomplishment update, using updated task:', error);
        // Continue with updatedTask if refresh fails
      }
      
      // Update local task state if needed
      onTaskSaved(refreshedTask);
      return refreshedTask;
    } catch (error) {
      console.error('Error updating actual accomplishment:', error);
      setSaveError(describeActionError(error, "Couldn't save the actual accomplishment. Try again."));
      return null;
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    // Reset input when task changes or modal closes
    if (!visible || !task) {
      setActualTotalValueInput(undefined);
      setIsActualTotalValueManuallyEdited(false);
    } else {
      const taskCosting = task.taskCostings?.[0];
      const lifecycleStatus = formData.status || task.status;
      if (taskCosting) {
        if (shouldForceActualTotalValueToZero(lifecycleStatus)) {
          setActualTotalValueInput('0.00');
        } else {
          setActualTotalValueInput(
            (taskCosting.actualTotalValue ?? taskCosting.totalValue ?? 0).toFixed(2)
          );
        }
        setIsActualTotalValueManuallyEdited(false);
      }
    }
  }, [visible, task, formData.status]);

  // Other Costs management functions
  const handleAddOtherCost = () => {
    // Generate a unique temporary ID for new costs
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Determine the most appropriate default date for the new other cost (UTC for consistency)
    let defaultDate = getCurrentUTCDateString();
    if (task) {
      const fromIdentified = normalizeToUTCDateString(task.identifiedDate);
      const fromCompletion = normalizeToUTCDateString(task.completionDate);
      if (fromIdentified) defaultDate = fromIdentified;
      else if (fromCompletion) defaultDate = fromCompletion;
    }
    
    setEditingOtherCost({
      id: tempId as any, // Set a temporary ID for new costs
      date: defaultDate,
      description: '',
      cost: '' as any,
      markupPercent: '' as any,
      markedUpCost: 0,
      comments: ''
    });
    setShowOtherCostModal(true);
  };

  const handleEditOtherCost = (cost: OtherCost) => {
    setEditingOtherCost(cost);
    setShowOtherCostModal(true);
  };

  const handleDeleteOtherCost = async (id: number | string | undefined) => {
    
    if (!id) {
      // A row without an id can't be matched to anything to delete; log only (developer error).
      console.error('Cannot delete cost: invalid ID');
      return;
    }
    
    if ((await confirmAction({ title: 'Delete Other Cost?', confirmLabel: 'Delete', destructive: true }))) {
      setOtherCosts(prev => {
        // Convert both IDs to strings for comparison to handle type mismatches
        const idStr = String(id);
        const filteredCosts = prev.filter(cost => String(cost.id || 'undefined') !== idStr);
        return filteredCosts;
      });
      
      // Add a timeout to check the state after React has processed the update
      setTimeout(() => {
      }, 100);
    } else {
    }
  };

  const handleSaveOtherCost = (cost: OtherCost) => {
    
    // Convert IDs to strings for comparison to handle type mismatches
    const costIdStr = cost.id ? String(cost.id) : null;
    const existingCost = otherCosts.find(c => String(c.id || 'undefined') === costIdStr);
    
    if (costIdStr && existingCost) {
      // Update existing cost in local state
      setOtherCosts(prev => prev.map(c => String(c.id || 'undefined') === costIdStr ? cost : c));
    } else {
      // Add new cost to local state
      // Ensure the cost has a unique ID
      const finalId = cost.id || `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const newCost = { ...cost, id: finalId };
      setOtherCosts(prev => [...prev, newCost]);
    }
    
    setShowOtherCostModal(false);
    setEditingOtherCost(null);
  };

  // Load other costs from task efforts (from task object or via API) when the Other Costs tab is opened
  const loadOtherCostsForTask = async () => {
    if (!task?.id) {
      return;
    }

    if (!hasPermission('quantified:other_costs_manage')) {
      return;
    }


    setOtherCostsLoading(true);
    setOtherCostsLoadError(false);

    try {
      let efforts = task.efforts;

      // If efforts are not present on the task object, fetch them from the API
      if (!efforts || efforts.length === 0) {
        efforts = await effortService.getTaskEfforts(task.id);
      }

      const otherEfforts = (efforts || []).filter(e => e.type === 'Other' || e.type === 'other');

      const loadedOtherCosts: OtherCost[] = otherEfforts.map((effort, index) => {
        // Ensure each cost has a valid ID
        const costId = effort.id || `loaded_${task.id}_${index}_${Date.now()}`;

        // Normalize date to YYYY-MM-DD in UTC to avoid timezone shifting
        const normalizedDate = normalizeToUTCDateString(effort.date) ?? getCurrentUTCDateString();

        const cost: OtherCost = {
          id: costId,
          date: normalizedDate,
          description: effort.description || '',
          cost: effort.quantity || 0,
          markupPercent: ((effort.rate || 0) * 100) - 100, // Convert rate back to markup percentage
          markedUpCost: (effort.quantity || 0) * (effort.rate || 1),
          comments: effort.comments || ''
        };
        return cost;
      });

      setOtherCosts(loadedOtherCosts);
      setOtherCostsLoadedTaskId(task.id);
    } catch (error) {
      console.error('Error loading other costs for task:', error);
      setOtherCosts([]);
      setOtherCostsLoadError(true);
    } finally {
      setOtherCostsLoading(false);
    }
  };

  // Trigger load when the Other Costs tab is opened for an existing task
  useEffect(() => {
    // When modal is closed, reset Other Costs state
    if (!visible) {
      setOtherCosts([]);
      setOtherCostsLoadedTaskId(null);
      setOtherCostsLoadError(false);
      setOtherCostsLoading(false);
      return;
    }

    // Only load when on the Other Costs tab and we have a task ID
    if (activeTab !== 'other_costs' || !task?.id) {
      return;
    }

    // Avoid reloading if we've already loaded Other Costs for this task (including when user deleted all)
    if (otherCostsLoadedTaskId === task.id) {
      return;
    }

    // Load Other Costs for the current task
    loadOtherCostsForTask();
    // We intentionally omit loadOtherCostsForTask from dependencies to avoid unnecessary recreation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, activeTab, task?.id, otherCostsLoadedTaskId]);

  // Refetch full task when Efforts tab opens so labour OT columns match DB (e.g. after form approval).
  useEffect(() => {
    if (!visible) {
      setEffortsTabRows([]);
      setEffortsTabLoading(false);
      return;
    }
    if (activeTab !== 'efforts' || !task?.id) {
      return;
    }
    let cancelled = false;
    setEffortsTabLoading(true);
    taskService
      .getTask(task.id)
      .then((t) => {
        if (!cancelled) setEffortsTabRows(t.efforts || []);
      })
      .catch(() => {
        if (!cancelled) setEffortsTabRows(task.efforts || []);
      })
      .finally(() => {
        if (!cancelled) setEffortsTabLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, activeTab, task?.id]);

  // Debug function to inspect current state
  const debugOtherCosts = () => {
  };

  const renderOtherCosts = () => {
    // Check permissions - only Ops Managers and Superintendents can access this tab
    const canManageOtherCosts = hasPermission('quantified:other_costs_manage');
    
    if (!canManageOtherCosts) {
      return (
        <Alert variant="warning">
          <i className="bi bi-exclamation-triangle tw-mr-2"></i>
          You don't have permission to access Other Costs management.
        </Alert>
      );
    }

    return (
      <>
        {otherCostsLoadError && (
          <div className="tw-mb-3">
            <LoadErrorState
              what="other costs"
              onRetry={() => {
                setOtherCostsLoadError(false);
                loadOtherCostsForTask();
              }}
            />
            <div className="tw-mt-1 tw-text-sm tw-text-content-muted">
              Existing Other Costs are left unchanged if you save without changing this tab.
            </div>
          </div>
        )}
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
          <h6 className="tw-m-0">Other Costs</h6>
          <div>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddOtherCost}
              disabled={isTaskLocked}
            >
              <i className="bi bi-plus-lg tw-mr-1"></i>
              Add Other Cost
            </Button>
          </div>
        </div>

        <Alert variant="info">
          <i className="bi bi-info-circle tw-mr-2"></i>
          Other costs will be added as efforts with type "Other" and will appear in the task's effort table.
        </Alert>

        {otherCosts.length === 0 ? (
          <div className="tw-text-center tw-text-content-muted tw-p-4">
            <i className="bi bi-currency-dollar tw-text-4xl tw-text-content-muted"></i>
            <p className="tw-mt-2">No other costs added yet</p>
            <small>Click "Add Other Cost" to add miscellaneous costs to this task</small>
          </div>
        ) : (
          <Table size="sm" bordered responsive>
              <thead className="tw-bg-surface">
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Cost</th>
                  <th>Markup %</th>
                  <th>Marked-up Cost</th>
                  <th>Comments</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {otherCosts.map((cost) => (
                  <tr key={cost.id}>
                    <td>{formatDateForDisplayUTC(cost.date)}</td>
                    <td>{cost.description}</td>
                    <td>{formatDollars(Number(cost.cost) || 0)}</td>
                    <td>{(Number(cost.markupPercent) || 0).toFixed(1)}%</td>
                    <td>{formatDollars(cost.markedUpCost)}</td>
                    <td>{cost.comments || '-'}</td>
                    <td>
                      <ButtonGroup>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditOtherCost(cost)}
                          disabled={isTaskLocked}
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteOtherCost(cost.id)}
                          disabled={isTaskLocked || !cost.id}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="tw-bg-surface-alt">
                <tr>
                  <td colSpan={2}><strong>Total</strong></td>
                  <td><strong>{formatDollars(otherCosts.reduce((sum, cost) => sum + (Number(cost.cost) || 0), 0))}</strong></td>
                  <td>-</td>
                  <td><strong>{formatDollars(otherCosts.reduce((sum, cost) => sum + cost.markedUpCost, 0))}</strong></td>
                  <td colSpan={2}>-</td>
                </tr>
              </tfoot>
            </Table>
        )}

        {/* Other Cost Modal */}
        {showOtherCostModal && editingOtherCost && (
          <OtherCostModal
            visible={showOtherCostModal}
            otherCost={editingOtherCost}
            onSave={handleSaveOtherCost}
            onCancel={() => {
              setShowOtherCostModal(false);
              setEditingOtherCost(null);
            }}
          />
        )}
      </>
    );
  };

  // Function to refresh task data without closing the modal
  const refreshTaskData = async () => {
    if (!task?.id) return;
    
    try {
      // Fetch updated task data using the already imported taskService
      const updatedTask = await taskService.getTask(task.id);
      onTaskSaved(updatedTask, { keepOpen: true });
    } catch (error) {
      console.error('Error refreshing task data:', error);
    }
  };

  if (!visible) return null;

  return (
    // 3xl: wide enough for the 12-column Efforts tab (W-M8). Full-screen sheet on a tablet, so
    // the form keeps most of the screen with the keyboard up (W-iP10).
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      size="3xl"
      tabletVariant="fullscreen"
      title={task ? (isTaskLocked ? 'View Task' : 'Edit Task') : addTitle}
      readOnly={Boolean(task) && isTaskLocked}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            {isTaskLocked ? 'Close' : 'Cancel'}
          </Button>
          {!isTaskLocked && (hasPermission('quantified:task_create') || hasPermission('quantified:task_edit')) && (
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              onClick={handleSubmit}
            >
              {loading ? 'Saving...' : (task ? 'Update' : 'Create')}
            </Button>
          )}
        </>
      }
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <StatusBar currentStatus={formData.status} />

        {saveError && (
          <Alert variant="danger" className="tw-mb-3" role="alert">
            <i className="bi bi-exclamation-circle tw-mr-2"></i>
            {saveError}
          </Alert>
        )}

        <ul className="tw-flex tw-flex-nowrap tw-overflow-x-auto tw-overflow-y-hidden tw-border-b tw-border-line tw-mb-3 tw-list-none tw-pl-0 tw-m-0 [&>li]:tw-shrink-0 [&_button]:tw-whitespace-nowrap">
          <li>
            <button
              className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'basic' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
              onClick={() => setActiveTab('basic')}
              type="button"
            >
              {tabLabel('Basic Info', 'basic')}
            </button>
          </li>
          <li>
            <button
              className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'planned_accomplishment' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
              onClick={() => setActiveTab('planned_accomplishment')}
              type="button"
            >
              {tabLabel('Planned Accomplishment', 'planned_accomplishment')}
            </button>
          </li>
          {/* Only show Actual Accomplishment tab for specific statuses */}
          {task && (task.status === TaskStage.INTERNAL_REVIEW || task.status === TaskStage.CLOSED || task.status === TaskStage.FINALIZED) ? (
            <li>
              <button
                className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'actual_accomplishment' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                onClick={() => setActiveTab('actual_accomplishment')}
                type="button"
              >
                Actual Accomplishment
              </button>
            </li>
          ) : null}
          <li>
            <button
              className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'location' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
              onClick={() => setActiveTab('location')}
              type="button"
            >
              {tabLabel('Location', 'location')}
            </button>
          </li>
          {/* Only show File Attachments tab when editing an existing task */}
          {task && task.id && (
            <li>
              <button
                className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'attachments' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                onClick={() => setActiveTab('attachments')}
                type="button"
              >
                File Attachments
                {/* Show camera icon indicator when there are photo attachments */}
                {(() => {
                  const hasPhotos = attachments.some(attachment =>
                    attachment.attachmentType === 'image' ||
                    attachment.attachmentName.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)
                  );
                  return hasPhotos ? (
                    <i className="bi bi-camera-fill tw-ml-2"
                       style={{ fontSize: '0.8em', color: '#0d6efd' }}
                       title="Contains photos"></i>
                  ) : null;
                })()}
              </button>
            </li>
          )}
          {task && task.id && (
            <li>
              <button
                className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'efforts' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                onClick={() => setActiveTab('efforts')}
                type="button"
              >
                Efforts
              </button>
            </li>
          )}
          {/* Only show Other Costs tab when editing an existing task and user has permission */}
          {task && task.id && hasPermission('quantified:other_costs_manage') && (
            <li>
              <button
                className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'other_costs' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                onClick={() => setActiveTab('other_costs')}
                type="button"
              >
                Other Costs
              </button>
            </li>
          )}
          {/* Show Related Work IDs tab when the task has linked Work IDs (click goes to filtered list) */}
          {showRelatedWorkIdsTab && (
            <li>
              <button
                className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'related_work_ids' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                onClick={() => setActiveTab('related_work_ids')}
                type="button"
              >
                Related Work IDs
              </button>
            </li>
          )}
        </ul>

        <div className="tw-pt-0">
          {activeTab === 'basic' && renderBasicInfo()}
          {activeTab === 'planned_accomplishment' && renderPlannedAccomplishment()}
          {activeTab === 'location' && renderLocation()}
          {activeTab === 'actual_accomplishment' && renderActualAccomplishment()}
          {activeTab === 'related_work_ids' && showRelatedWorkIdsTab && task?.id && (
            <RelatedWorkIdsTab
              workIds={task.workIds}
              taskId={task.id}
              onUnlink={hasPermission('workId:assign') ? refreshTaskData : undefined}
            />
          )}
          {activeTab === 'attachments' && renderAttachments()}
          {activeTab === 'efforts' && task?.id && (
            <div>
              <p className="tw-text-content-muted tw-text-sm tw-mb-2">
                Labour, materials, vehicles, and other costs from forms and entries. OT Breakdown shows DRM straight / 1.5× / 2.0× hours when saved on the effort.
              </p>
              {effortsTabLoading ? (
                <div className="tw-text-center tw-py-4 tw-text-content-muted">Loading efforts…</div>
              ) : (
                <TaskEffortsTable
                  efforts={effortsTabRows}
                  className="tw-mb-0"
                  columns={{
                    type: true,
                    date: true,
                    employeeName: true,
                    description: true,
                    accomplishment: false,
                    quantity: true,
                    unitOfMeasure: true,
                    rate: true,
                    fuelSurcharge: true,
                    totalCost: true,
                    comments: true,
                    otBreakdown: true,
                    timecard: true
                  }}
                  canToggleVehicleFuel={hasPermission('quantified:task_edit')}
                  onVehicleFuelEffortPatched={(effort) => {
                    setEffortsTabRows((prev) =>
                      prev.map((e) => (e.id === effort.id ? { ...e, ...effort } : e))
                    );
                  }}
                />
              )}
            </div>
          )}
          {activeTab === 'other_costs' && renderOtherCosts()}
        </div>
      </form>

      {/* Nested confirm — Headless UI's dialog stack paints it above the parent TaskModal. */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setDeletingAttachmentId(null);
        }}
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Attachment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-mb-0">Are you sure you want to delete this attachment?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteConfirm(false);
              setDeletingAttachmentId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </ResponsiveModal>
  );
};

// Other Cost Modal Component
type OtherCostField = 'date' | 'description' | 'cost' | 'markupPercent';
const OTHER_COST_FIELDS: OtherCostField[] = ['date', 'description', 'cost', 'markupPercent'];
const otherCostFieldId = (field: OtherCostField) => `other-cost-${field}`;

interface OtherCostModalProps {
  visible: boolean;
  otherCost: OtherCost;
  onSave: (cost: OtherCost) => void;
  onCancel: () => void;
}

const OtherCostModal: React.FC<OtherCostModalProps> = ({
  visible,
  otherCost,
  onSave,
  onCancel
}) => {
  const [formData, setFormData] = useState<OtherCost>(otherCost);
  const [saving, setSaving] = useState(false);
  /** Per-field validation errors, shown under each field (FORM-01). */
  const [errors, setErrors] = useState<Partial<Record<OtherCostField, string>>>({});

  useEffect(() => {
    setFormData(otherCost);
    setErrors({});
  }, [otherCost]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (errors[name as OtherCostField]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name as OtherCostField];
        return next;
      });
    }
    let updatedFormData = { ...formData, [name]: value };

    // Recalculate marked-up cost when cost or markup changes
    if (name === 'cost' || name === 'markupPercent') {
      const cost = Number(name === 'cost' ? value : updatedFormData.cost) || 0;
      const markupPercent = Number(name === 'markupPercent' ? value : updatedFormData.markupPercent) || 0;
      updatedFormData.markedUpCost = cost * (1 + markupPercent / 100);
      
      // Keep the actual input value for the field being changed to preserve user input
      if (name === 'cost') {
        updatedFormData.cost = value; // Keep as string to preserve user input
      } else if (name === 'markupPercent') {
        updatedFormData.markupPercent = value; // Keep as string to preserve user input
      }
    }

    setFormData(updatedFormData);
  };

  const handleSubmit = async () => {
    // Validation (FORM-01): every error under its field, focus the first one.
    const nextErrors: Partial<Record<OtherCostField, string>> = {};
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!formData.date || !formData.date.trim()) {
      nextErrors.date = 'Date required';
    } else if (!dateRegex.test(formData.date)) {
      nextErrors.date = 'Enter a valid date';
    }
    if (!formData.description.trim()) {
      nextErrors.description = 'Description required';
    }
    if (!formData.cost) {
      nextErrors.cost = 'Cost required';
    } else if (Number(formData.cost) <= 0) {
      nextErrors.cost = 'Cost must be more than 0';
    }
    if (Number(formData.markupPercent) < 0) {
      nextErrors.markupPercent = "Markup can't be negative";
    }
    const firstInvalid = OTHER_COST_FIELDS.find((f) => nextErrors[f]);
    if (firstInvalid) {
      setErrors(nextErrors);
      setTimeout(() => document.getElementById(otherCostFieldId(firstInvalid))?.focus(), 0);
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error in onSave:', error);
    } finally {
      setSaving(false);
    }
  };

  // Nested inside TaskModal. Previously forced to zIndex 1060 to outrank the parent; the
  // Modal primitive's dialog stack handles that ordering, so no manual z-index is needed.
  return (
    <Modal show={visible} onHide={onCancel}>
      <Modal.Header closeButton>
        <Modal.Title>
          {formData.id ? 'Edit Other Cost' : 'Add Other Cost'}
        </Modal.Title>
      </Modal.Header>
          <Modal.Body>
            <Form.Group className="tw-mb-3">
              <Form.Label>Date *</Form.Label>
              <Form.Control
                type="date"
                name="date"
                id={otherCostFieldId('date')}
                isInvalid={!!errors.date}
                aria-describedby={errors.date ? `${otherCostFieldId('date')}-error` : undefined}
                value={formData.date}
                onChange={handleChange}
                required
              />
              {errors.date && (
                <Form.Control.Feedback type="invalid" className="tw-block" id={`${otherCostFieldId('date')}-error`}>
                  {errors.date}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="tw-mb-3">
              <Form.Label>Cost Description *</Form.Label>
              <Form.Control
                type="text"
                name="description"
                id={otherCostFieldId('description')}
                isInvalid={!!errors.description}
                aria-describedby={errors.description ? `${otherCostFieldId('description')}-error` : undefined}
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description of the cost"
                required
              />
              {errors.description && (
                <Form.Control.Feedback type="invalid" className="tw-block" id={`${otherCostFieldId('description')}-error`}>
                  {errors.description}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="tw-mb-3">
                  <Form.Label>Cost *</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      name="cost"
                      id={otherCostFieldId('cost')}
                      isInvalid={!!errors.cost}
                      aria-describedby={errors.cost ? `${otherCostFieldId('cost')}-error` : undefined}
                      value={formData.cost}
                      onChange={handleChange}
                      placeholder="Enter cost amount"
                      required
                    />
                  </InputGroup>
                  {errors.cost && (
                    <Form.Control.Feedback type="invalid" className="tw-block" id={`${otherCostFieldId('cost')}-error`}>
                      {errors.cost}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="tw-mb-3">
                  <Form.Label>Markup %</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      step="0.1"
                      min="0"
                      name="markupPercent"
                      id={otherCostFieldId('markupPercent')}
                      isInvalid={!!errors.markupPercent}
                      aria-describedby={errors.markupPercent ? `${otherCostFieldId('markupPercent')}-error` : undefined}
                      value={formData.markupPercent}
                      onChange={handleChange}
                      placeholder="Enter markup percentage"
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                  {errors.markupPercent && (
                    <Form.Control.Feedback type="invalid" className="tw-block" id={`${otherCostFieldId('markupPercent')}-error`}>
                      {errors.markupPercent}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="tw-mb-3">
              <Form.Label>Marked-up Cost</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="text"
                  value={formData.markedUpCost.toFixed(2)}
                  readOnly
                  style={{ backgroundColor: '#e9ecef' }}
                />
              </InputGroup>
              <small className="tw-text-content-muted">
                Calculated as: Cost × (1 + Markup%)
              </small>
            </Form.Group>

            <Form.Group className="tw-mb-3">
              <Form.Label>Comments/Notes</Form.Label>
              <Form.Control
                as="textarea"
                name="comments"
                value={formData.comments || ''}
                onChange={handleChange}
                rows={3}
                placeholder="Additional notes or comments..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="tw-mr-2" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                `${formData.id ? 'Update' : 'Add'} Other Cost`
              )}
            </Button>
          </Modal.Footer>
    </Modal>
  );
};

export default TaskModal;
